import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";

import moment from "moment";
import { format, addMonths } from "date-fns";

import CancelIcon from "@mui/icons-material/Cancel";
import { ReactComponent as RightArrow } from "../../icons/ScrollRightArrow.svg";
import { ReactComponent as LeftArrow } from "../../icons/ScrollLeftArrow.svg";
import { ReactComponent as VisaCardIcon } from '../../icons/visaCardIcon.svg';
import { ReactComponent as MasterCardIcon } from '../../icons/masterCardIcon.svg';
import { ReactComponent as AmexCardIcon } from '../../icons/amexCardIcon.svg';
import { ReactComponent as DiscoverCardIcon } from '../../icons/discoverCard.svg';
import { ReactComponent as CashappIcon } from '../../icons/cashappIcon.svg';
import { ReactComponent as AmazonPayIcon } from '../../icons/AmazonPayIcon.svg';
// import { ReactComponent as CheckMarkIcon } from "../../icons/CheckMarkIcon.svg";
import { useGetPaymentMethods } from '../../hooks/paymenthooks'
import { extractPlanFields, planRecurrenceMap, styleThousandCommas, formatMaxHours, getMemberBookingPlanIdForResource, getMemberBookingBalanceForResource, isFirstBookingWithPlan, capitalizeString, calculateMixedTimeBlockCost, expandRecurrenceDates, BOOKING_RECURRENCE_OPTIONS, getDatesWithTimeBlockIssues, formatMultiDateTimeBlockError, getRecurrenceControlsFromSelectedDays, getSubmissionEndRecurrence, isKitchenClosedOnDate, normalizeTeamMembers, parseMemberInventoryLimits, shouldApplyMemberInventoryLimits, calculateInventoryCostLinesWithLimits } from '../../utils/helpers';
import { AddPaymentMethodModal, ViewPlanDetailsModal } from "./AppModal";
// import { InviteTeamMemberModal } from "../../components/InviteTeamMemberModal";
import AddTeamMembersView, { getResourceCapacityLimits, getTeamMemberDisplayName } from "../../components/AddTeamMembersView";
import UserAvatar from "../../components/Mobile/UserAvatar";

// import { useAuthState } from "../../contexts/authcontext";

import {
  // BookingSchema,
  BookingRecurrenceType,
  MembershipSchema,
  ResourceSchema,
  ResourceType,
  ResourceBookingSchema,
  ResourcePlanSchema,
  FeeSchema,
  TimeBlock,
  DiscountSchema,
  TeamMemberSchema,
  ResourceInventorySchema,
  InventorySchema,
  OrderItemSchema,
} from "../../data/types";
import ReservationCalendar from "./ReservationCalendar";
import TimesView from "./AvailableTimes";
import { ArrowBackIos, Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { ReactComponent as HourGlassIcon } from '../../icons/HourGlass.svg';
import { ReactComponent as CalendarMonthIcon } from '../../icons/calendarIconBold.svg';
import { ReactComponent as AccessTimeIcon } from '../../icons/timeIconBold.svg';
import { useCompleteSignIn, useLoadBookings, useLoadResourceBookings,
        // useLoadBookings 
        } from "../../hooks/authhooks";
import { PaymentMethod } from "@stripe/stripe-js";
// import { getMembershipPlans } from "../api/crud";
// import StripePaymentCheckOut from "./PaymentCheckOut";
import { toast } from "sonner";
import SubmitRequestButton from '../../components/SubmitRequestButton';
import {
  InventoryOutOfStockBadge,
  InventoryOutOfStockCheckboxSlot,
  InventoryOutOfStockImage,
  inventoryOutOfStockCardClassName,
  inventoryOutOfStockDescription,
  formatInventoryRemainingLabel,
  formatInventoryUnitPriceLabel,
} from '../../components/InventoryOutOfStockElements';
import {
  getInventoryItemRemainingForSessions,
  getInventorySessionWindows,
  type InventoryStockBooking,
} from '../../utils/helpers/inventoryStockStatus';

type StartBookingProps = {
  // isMember?: MembershipSchema;
  isOpen?: boolean;
  resourceDetail: ResourceSchema;
  currentMember: MembershipSchema;
  selectedPlan: ResourcePlanSchema;
  handleClose: () => void;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitRequest: (
    resource: ResourceSchema,
    resourceType: ResourceType,
    booking: ResourceBookingSchema
  ) => Promise<boolean>;
  handleSubmitMultipleRequest: (
    resource: ResourceSchema,
    resourceType: ResourceType,
    bookings: ResourceBookingSchema[]
  ) => Promise<boolean>;
  inventoryOnlyItem?: InventorySchema;
};

const getNextBookingStep = (current: number, excludesSpace: boolean): number => {
  if (!excludesSpace) {
    return Math.min(current + 1, 6);
  }
  if (current === 3) {
    return 5;
  }
  return Math.min(current + 1, 6);
};

const getPreviousBookingStep = (current: number, excludesSpace: boolean): number => {
  if (!excludesSpace) {
    return Math.max(current - 1, 1);
  }
  if (current === 5) {
    return 3;
  }
  return Math.max(current - 1, 2);
};

const getBookingProgressIndex = (current: number, excludesSpace: boolean): number => {
  if (!excludesSpace) {
    return current;
  }
  const inventoryOnlyProgress: Record<number, number> = { 2: 1, 3: 2, 5: 3, 6: 4 };
  return inventoryOnlyProgress[current] ?? 1;
};

const BOOKING_PROGRESS_TOTAL = (excludesSpace: boolean) => (excludesSpace ? 4 : 6);

type BookingSelection = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  totalHours: number;
};

// type TeamMember = TeamMemberSchema;

const getInitialTeamMembers = (member?: MembershipSchema): TeamMemberSchema[] =>
  normalizeTeamMembers(member?.team_members ?? []);

// const getTeamMemberInitials = (member: TeamMemberSchema) => {
//   const first = member.first_name?.[0] || '';
//   const last = member.last_name?.[0] || '';
//   return (first + last).toUpperCase() || member.email?.[0]?.toUpperCase() || '?';
// };

const getNext30MinuteInterval = (dateTime?: any) => {
  dateTime = dateTime ? moment(dateTime) : moment(); // 2023-06-07T08:54 vs 2023-06-07T08:10
  var minutes = dateTime.minutes();
  var remainder = 30 - minutes;
  var next30MinMark = dateTime.minutes(0);
  if (remainder < 0) {
    next30MinMark = dateTime.minutes(minutes + remainder);
    next30MinMark.add(30, "minutes");
  } else if (remainder < 30) {
    next30MinMark.minutes(minutes + remainder);
  }
  return next30MinMark.format("YYYY-MM-DDTHH:mm");
};

const formatLongBookingDateLabel = (dateStr: string) =>
  moment(dateStr).format("ddd, MMMM D, YYYY");

/** True when sorted YYYY-MM-DD strings are day-by-day consecutive (e.g. Mon→Tue→Wed). */
const areSortedBookingDatesConsecutive = (sortedDateStrs: string[]): boolean => {
  if (sortedDateStrs.length <= 1) return true;
  for (let i = 1; i < sortedDateStrs.length; i++) {
    const stepDays = moment(sortedDateStrs[i])
      .startOf("day")
      .diff(moment(sortedDateStrs[i - 1]).startOf("day"), "days");
    if (stepDays !== 1) return false;
  }
  return true;
};

// CreateBookingProps> = ({resourceDetail,
const RequestKitchen: React.FC<StartBookingProps> = ({
  isOpen,
  resourceDetail,
  currentMember,
  setIsSubmitting,
  handleClose,
  handleSubmitRequest,
  handleSubmitMultipleRequest,
  selectedPlan,
  inventoryOnlyItem,
}) => {
  const excludesSpace = Boolean(inventoryOnlyItem);
  const [plans, setCurrentPlans] = useState<ResourcePlanSchema[]>();
  const { user } = useCompleteSignIn();
  const loggedinUid = (user && user?.uid) ? user.uid : ''
  // const [resourceOptions, setResourceOptions] = useState<any>();
  const [availableTimeBlocks, setAvailableTimeBlocks] = useState<TimeBlock[]>(); 
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [selectedDays, setSelectedDays] = useState<Date[]>([]);
  const [hasSelectedDate, setHasSelectedDate] = useState(false);
  const [hasInitiatedCalendarSelection, setHasInitiatedCalendarSelection] = useState(false);
  const [activeBookingDetailStep, setActiveBookingDetailStep] = useState<number | null>(null);
  /** Which hours/date/time row is highlighted in the overview (sub-steps 2–4). */
  const [bookingActiveSection, setBookingActiveSection] = useState<number>(2);

  // const handleDateChange = (date: Date) => {
  //   setSelectedDay(date);
  //   setCurrentDate(date);
  // }

  // console.log(resourceDetail);

  // const handleNHoursChange = (currentStart: Date, currentEnd: Date) => {
  //   var duration = moment.duration(
  //     moment(currentEnd)?.diff(moment(currentStart))
  //   );
  //   // calculate hours diff
  //   var hoursDiff = duration.asHours();
  //   if (!isNaN(hoursDiff) && hoursDiff >= 0) {
  //     return hoursDiff;
  //   }
  //   return 0;
  // };

  var view = "week";
  const [currentStep, updateCurrentStep] = useState<number>(excludesSpace ? 2 : 1);
  // const [currentBooking, updateCurrentBooking] = useState<ResourceBookingSchema>();
  const [startDate, updateStartDate] = useState(getNext30MinuteInterval());
  const [endDate, updateEndDate] = useState(startDate);

  // const [startDate, updateStartDate] = useState(new Date());
  // const [endDate, updateEndDate] = useState(new Date());
  const [nHours, updateNHours] = useState(0);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Helper function above component
  // const extractPlanFields = (member: any): PlanSchema => {
  //   if (!member) return {};
    
  //   return Object.entries(member)
  //     .filter(([key]) => key in (member as PlanSchema))
  //     .reduce((acc, [key, value]) => ({
  //       ...acc,
  //       [key]: value
  //     }), {});
  // };

  // const currentPlan = extractPlanFields(currentMember);
  if (!selectedPlan) {
    selectedPlan = extractPlanFields(currentMember);
  }
  const [plan, setPlan] = useState<ResourcePlanSchema>(selectedPlan);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [availableTeamMembers, setAvailableTeamMembers] = useState<TeamMemberSchema[]>(() =>
    getInitialTeamMembers(currentMember)
  );
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState<string[]>([]);
  const [orderInventory, setOrderInventory] = useState<OrderItemSchema[]>([]);

  const availableInventoryItems = useMemo(() => {
    const inventoryCatalogItems = (resourceDetail?.resource_inventory ?? {}) as ResourceInventorySchema;
    const catalogOptions = Object.keys(inventoryCatalogItems);
    const catalogOption = catalogOptions.length > 0 ? catalogOptions[0] : "";
    return (inventoryCatalogItems[catalogOption] || []) as InventorySchema[];
  }, [resourceDetail?.resource_inventory]);

  const memberTeamMembersKey = useMemo(
    () => JSON.stringify(currentMember?.team_members ?? []),
    [currentMember?.team_members]
  );

  useEffect(() => {
    setAvailableTeamMembers(getInitialTeamMembers(currentMember));
    setSelectedTeamMemberIds([]);
  }, [memberTeamMembersKey, currentMember]);


  // const loggedin_username = (user && user?.username) ? user.username : '';
  var { paymentMethods } = useGetPaymentMethods(loggedinUid);
  paymentMethods = (paymentMethods && Array.isArray(paymentMethods)) ? paymentMethods : [];
  const { bookings: userBookings } = useLoadBookings(currentMember?.uid || "");
  const { resourceBookings } = useLoadResourceBookings(
    resourceDetail?.community_id || "",
    resourceDetail?.id || ""
  );
  const availabilityBookings = useMemo(() => {
    const combined = [...(userBookings ?? []), ...(resourceBookings ?? [])];
    return combined.filter(
      (booking, index, self) => index === self.findIndex((b) => b.id === booking.id)
    );
  }, [userBookings, resourceBookings]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    (paymentMethods && paymentMethods.length > 0) ? paymentMethods[paymentMethods.length - 1] : {} as PaymentMethod
  );
  const [selectedBlock, setSelectedBlock] = useState<TimeBlock>();
  const [bookingSelections, setBookingSelections] = useState<BookingSelection[]>([]);

  const [bookingRecurrence, setBookingRecurrence] = useState<BookingRecurrenceType>('once');
  const recurrenceAnchorRef = useRef<Date | null>(null);
  const recurrenceSourceRef = useRef<'calendar' | 'controls' | null>(null);
  const [endRecurrence, setEndRecurrence] = useState<string>(() =>
    moment().add(1, 'month').format('YYYY-MM-DD')
  );

  const expandRecurrenceOptions = useMemo(
    () => ({
      resource: resourceDetail,
      nHours,
      bookings: availabilityBookings,
    }),
    [resourceDetail, nHours, availabilityBookings]
  );

  const [isEditing, setIsEditing] = useState(false);

  
  // console.log("isEditing resourceDetail", isEditing, resourceDetail)

  const checkDiscountActive = useCallback((discount: DiscountSchema): boolean => {
    if (!discount?.rate) return false;
    if (!startDate || !endDate) return false;
    
    const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
    const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
    const bookingStart = moment(startDate).startOf('day');
    const bookingEnd = moment(endDate).endOf('day');
    
    // Check if the booking period falls entirely within the discount period
    const isValid = bookingStart.isSameOrAfter(discountStartDate) && 
                    bookingStart.isSameOrBefore(discountEndDate) &&
                    bookingEnd.isSameOrAfter(discountStartDate) &&
                    bookingEnd.isSameOrBefore(discountEndDate);
    return isValid;
  }, [startDate, endDate]);

  const allBookingSelections = useMemo<BookingSelection[]>(() => {
    const currentSelectionDates = hasInitiatedCalendarSelection
      ? selectedDays.length > 0
        ? selectedDays
        : hasSelectedDate
          ? [selectedDay]
          : []
      : [];
    const hasValidWindow = startDate && endDate && moment(endDate).isAfter(moment(startDate));
    const currentStartTime = hasValidWindow ? moment(startDate).format("HH:mm") : "";
    const currentEndTime = hasValidWindow ? moment(endDate).format("HH:mm") : "";
    const currentSelections: BookingSelection[] =
      nHours > 0 && hasValidWindow
        ? currentSelectionDates.map((date) => ({
            id: `${moment(date).format("YYYY-MM-DD")}-${currentStartTime}-${currentEndTime}`,
            date: moment(date).format("YYYY-MM-DD"),
            startTime: currentStartTime,
            endTime: currentEndTime,
            totalHours: nHours,
          }))
        : [];

    const allSelectionsMap = new Map<string, BookingSelection>();
    [...bookingSelections, ...currentSelections].forEach((selection) => {
      allSelectionsMap.set(selection.id, selection);
    });

    return Array.from(allSelectionsMap.values()).sort(
      (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
    );
  }, [bookingSelections, endDate, hasInitiatedCalendarSelection, hasSelectedDate, nHours, selectedDay, selectedDays, startDate]);

  useEffect(() => {
    setOrderInventory((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      return expandOrderInventoryForSelections(
        prev,
        allBookingSelections,
        availableInventoryItems,
        resourceDetail.name || ''
      );
    });
  }, [allBookingSelections, availableInventoryItems, resourceDetail.name]);

  useEffect(() => {
    if (!inventoryOnlyItem) {
      return;
    }
    if (allBookingSelections.length === 0) {
      setOrderInventory([]);
      return;
    }
    const template: OrderItemSchema = {
      ...inventoryOnlyItem,
      id: getInventoryOrderItemId(inventoryOnlyItem, allBookingSelections[0].id),
      inventory_id: inventoryOnlyItem.id || '',
      total_quantity: 1,
      available_quantity: inventoryOnlyItem.available_quantity,
      resource_name: resourceDetail.name || '',
      item_name: inventoryOnlyItem.item_name,
      category: inventoryOnlyItem.category,
      resource_type: inventoryOnlyItem.resource_type,
    };
    setOrderInventory(
      expandOrderInventoryForSelections(
        [template],
        allBookingSelections,
        availableInventoryItems,
        resourceDetail.name || ''
      )
    );
  }, [
    allBookingSelections,
    availableInventoryItems,
    inventoryOnlyItem,
    resourceDetail.name,
  ]);

  const resetBookingForm = () => {
    updateStartDate("")
    updateEndDate("")
    updateNHours(0)
    setPlan({} as ResourcePlanSchema)
    setAdditionalNotes("")
    setSelectedBlock(undefined)
    setSelectedDays([])
    setHasSelectedDate(false)
    setHasInitiatedCalendarSelection(false)
    setBookingSelections([])
    setActiveBookingDetailStep(null)
    setBookingActiveSection(2)
    updateCurrentStep(excludesSpace ? 2 : 1)
    setBookingRecurrence('once')
    setEndRecurrence(moment().add(1, 'month').format('YYYY-MM-DD'))
    recurrenceAnchorRef.current = null
    setOrderInventory([])
    setSelectedTeamMemberIds([])
  }

  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);

  const handleConfirmBooking = async () => {
    if (isSubmittingBooking) return;
    // console.log("handleConfirmBooking startDate", startDate)
    // console.log("handleConfirmBooking endDate", endDate)
    // console.log("handleConfirmBooking resourceDetail", resourceDetail)
    if (!(user && resourceDetail && loggedinUid && startDate && endDate)) return;

    const allSelections = allBookingSelections;

    if (allSelections.length === 0) {
      toast.error("Booking submission failed", {
        description: "Please select at least one date and time.",
        duration: 5000,
        position: "top-right",
        className: "bg-red-500 text-white",
        icon: "🚫",
      });
      return;
    }

    const selectedBookingTeamMembers = availableTeamMembers.filter((member) =>
      selectedTeamMemberIds.includes(member.id)
    );

    const createBooking = (selection: BookingSelection): ResourceBookingSchema => {
      const selectionStart = moment(`${selection.date}T${selection.startTime}`);
      let selectionEnd = moment(`${selection.date}T${selection.endTime}`);
      if (!selectionEnd.isAfter(selectionStart)) {
        selectionEnd = selectionEnd.add(1, "day");
      }

      return {
        resource_type: "kitchen",
        community_id: resourceDetail.community_id ? resourceDetail.community_id : "",
        resource_id: resourceDetail.id ? resourceDetail.id : "",
        uid: loggedinUid,
        start_time: selectionStart.format("YYYY-MM-DDTHH:mm").toString(),
        end_time: selectionEnd.format("YYYY-MM-DDTHH:mm").toString(),
        location: resourceDetail.address
          ? resourceDetail.address
          : resourceDetail.country
          ? resourceDetail.country
          : "",
        has_reminder: false,
        notes: additionalNotes,
        total_hours: selection.totalHours,
        plan_id: plan?.plan_id,
        payment_method: paymentMethod?.id,
        fees: resourceDetail?.fees,
        discounts: resourceDetail
          ? resourceDetail?.discounts?.filter((discount: DiscountSchema) => checkDiscountActive(discount))
          : ([] as DiscountSchema[]),
        team_members: selectedBookingTeamMembers,
        ...(excludesSpace ? { excludes_space: true } : {}),
        ...(() => {
          const selectionOrderInventory = getOrderInventoryForSelection(
            orderInventory,
            selection.id
          );
          return selectionOrderInventory.length > 0
            ? { order_inventory: selectionOrderInventory }
            : {};
        })(),
      };
    };

    let success = false;

    setIsSubmittingBooking(true);
    try {
    const { recurrence: submissionRecurrence, endRecurrence: submissionEnd } =
      getRecurrenceControlsFromSelectedDays(selectedDays);

    const isRecurringBooking =
      submissionRecurrence !== 'once' &&
      Boolean(submissionEnd) &&
      allSelections.length > 0;

    if (isRecurringBooking) {
      const firstSelection = allSelections[0];
      const submissionEndRecurrence = getSubmissionEndRecurrence(
        submissionEnd!,
        selectedDays
      );
      const booking = createBooking(firstSelection);
      booking.booking_recurrence = submissionRecurrence;
      booking.end_recurrence = submissionEndRecurrence;
      booking.recurrence_count = allSelections.length;
      success = await handleSubmitRequest(resourceDetail, "kitchen", booking);
    } else if (allSelections.length > 1) {
      const bookings = allSelections.map((selection) => {
        const booking = createBooking(selection);
        booking.booking_recurrence = "once";
        booking.recurrence_count = 1;
        booking.end_recurrence = selection.date;
        return booking;
      });
      success = await handleSubmitMultipleRequest(resourceDetail, "kitchen", bookings);
    } else {
      const booking = createBooking(allSelections[0]);
      booking.booking_recurrence = "once";
      booking.recurrence_count = 1;
      booking.end_recurrence = allSelections[0].date;
      success = await handleSubmitRequest(resourceDetail, "kitchen", booking);
    }

    if(success){
      setIsSubmitting(true);
      resetBookingForm()
      handleClose()
      toast.success("Booking submitted successfully", {
        description: "You will receive an email confirmation shortly.",
        duration: 5000,
        position: "top-right",
        className: "bg-green-500 text-white",
        icon: "🎉",
      })
    } else {
      toast.error("Booking submission failed", {
        description: "Please try a different day / time.",
        duration: 5000,
        position: "top-right",
        className: "bg-red-500 text-white",
        icon: "🚫",
      })
    }
    } finally {
      setIsSubmittingBooking(false);
    }
   
  };
  // console.log("startDate: ", startDate);
  // console.log("endDate: ", endDate);

  const [isChangingPlan, setIsChangingPlan] = useState(false);

  const handleSelectedDayChange = useCallback((day: Date) => {
    if (resourceDetail && isKitchenClosedOnDate(day, resourceDetail)) {
      toast.error('This date is closed.', {
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    recurrenceSourceRef.current = 'calendar';
    setHasInitiatedCalendarSelection(true);
    setSelectedDay(day);

    setSelectedDays((prev) => {
      if (bookingRecurrence !== 'once' && endRecurrence && prev.length === 0) {
        const expanded = expandRecurrenceDates(
          day,
          endRecurrence,
          bookingRecurrence,
          expandRecurrenceOptions
        );
        const next = expanded.length > 0 ? expanded : [day];
        setHasSelectedDate(next.length > 0);
        recurrenceAnchorRef.current = next[0] ?? null;
        return next;
      }

      const exists = prev.some((selected) => moment(selected).isSame(day, 'day'));
      const next = exists
        ? prev.filter((selected) => !moment(selected).isSame(day, 'day'))
        : [...prev, day].sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
      setHasSelectedDate(next.length > 0);
      if (next.length === 0) {
        recurrenceAnchorRef.current = null;
      } else {
        recurrenceAnchorRef.current = next[0];
      }
      return next;
    });
  }, [bookingRecurrence, endRecurrence, expandRecurrenceOptions, resourceDetail]);

  useEffect(() => {
    if (recurrenceSourceRef.current !== 'calendar') {
      return;
    }
    recurrenceSourceRef.current = null;

    if (!hasInitiatedCalendarSelection) {
      return;
    }

    const { recurrence, endRecurrence: end } =
      getRecurrenceControlsFromSelectedDays(selectedDays);

    if (selectedDays.length > 0) {
      recurrenceAnchorRef.current = [...selectedDays].sort(
        (a, b) => moment(a).valueOf() - moment(b).valueOf()
      )[0];
    }

    setBookingRecurrence(recurrence);
    if (recurrence === 'once') {
      return;
    }
    if (end) {
      setEndRecurrence(end);
    }
  }, [selectedDays, hasInitiatedCalendarSelection]);

  const ensureRecurrenceAnchor = useCallback(() => {
    if (recurrenceAnchorRef.current) {
      return recurrenceAnchorRef.current;
    }
    if (selectedDays.length > 0) {
      const anchor = [...selectedDays].sort(
        (a, b) => moment(a).valueOf() - moment(b).valueOf()
      )[0];
      recurrenceAnchorRef.current = anchor;
      return anchor;
    }
    recurrenceAnchorRef.current = selectedDay;
    return selectedDay;
  }, [selectedDay, selectedDays]);

  const handleBookingRecurrenceChange = useCallback(
    (value: BookingRecurrenceType) => {
      recurrenceSourceRef.current = 'controls';
      if (value !== 'once' && hasInitiatedCalendarSelection && selectedDays.length > 0) {
        ensureRecurrenceAnchor();
      }
      if (value !== 'once' && !endRecurrence) {
        const defaultEnd = moment().add(1, 'month').format('YYYY-MM-DD');
        if (selectedDays.length > 0) {
          const lastSelected = [...selectedDays].sort(
            (a, b) => moment(a).valueOf() - moment(b).valueOf()
          )[selectedDays.length - 1];
          setEndRecurrence(
            moment.max(moment(lastSelected), moment(defaultEnd)).format('YYYY-MM-DD')
          );
        } else {
          setEndRecurrence(defaultEnd);
        }
      }
      setBookingRecurrence(value);
    },
    [ensureRecurrenceAnchor, endRecurrence, hasInitiatedCalendarSelection, selectedDays]
  );

  const handleEndRecurrenceChange = useCallback(
    (value: string) => {
      recurrenceSourceRef.current = 'controls';
      if (hasInitiatedCalendarSelection && selectedDays.length > 0) {
        ensureRecurrenceAnchor();
      }
      setEndRecurrence(value);
    },
    [ensureRecurrenceAnchor, hasInitiatedCalendarSelection, selectedDays.length]
  );

  const handleClearSelectedDays = useCallback(() => {
    recurrenceSourceRef.current = 'controls';
    recurrenceAnchorRef.current = null;
    setSelectedDays([]);
    setHasSelectedDate(false);
    setBookingRecurrence('once');
  }, []);

  useEffect(() => {
    if (recurrenceSourceRef.current !== 'controls') {
      return;
    }
    recurrenceSourceRef.current = null;

    if (!hasInitiatedCalendarSelection || selectedDays.length === 0) {
      return;
    }

    if (bookingRecurrence === 'once') {
      return;
    }
    if (!endRecurrence) {
      setSelectedDays([]);
      setHasSelectedDate(false);
      return;
    }

    const anchor = ensureRecurrenceAnchor();
    if (moment(anchor).startOf('day').isAfter(moment(endRecurrence).startOf('day'))) {
      setSelectedDays([]);
      setHasSelectedDate(false);
      return;
    }

    const expanded = expandRecurrenceDates(
      anchor,
      endRecurrence,
      bookingRecurrence,
      expandRecurrenceOptions
    );
    if (expanded.length > 0) {
      recurrenceAnchorRef.current = expanded[0];
      setSelectedDays(expanded);
      setHasSelectedDate(true);
      setHasInitiatedCalendarSelection(true);
      const latestEnd = moment(expanded[expanded.length - 1]).format('YYYY-MM-DD');
      if (latestEnd !== endRecurrence) {
        setEndRecurrence(latestEnd);
      }
    } else {
      setSelectedDays([]);
      setHasSelectedDate(false);
    }
  }, [
    bookingRecurrence,
    endRecurrence,
    ensureRecurrenceAnchor,
    expandRecurrenceOptions,
    hasInitiatedCalendarSelection,
    selectedDays.length,
  ]);



  useEffect(() => {
    setCurrentPlans(resourceDetail?.plans)
    setPlan(selectedPlan as ResourcePlanSchema)
    setPaymentMethod((paymentMethods && paymentMethods.length > 0) ? paymentMethods[paymentMethods.length - 1] : {} as PaymentMethod)  

    if (selectedPlan?.plan_recurrence === 'daily' && selectedPlan?.plan_max_hours) {
      updateNHours(selectedPlan.plan_max_hours);
    }
    // eslint-disable-next-line 
  }, [selectedPlan, resourceDetail, resourceDetail.community_id])

  // Ensure selected payment method stays in sync when methods refresh
  useEffect(() => {
    const hasMethods = paymentMethods && paymentMethods.length > 0;
    const currentId = (paymentMethod as any)?.id;
    const stillExists = hasMethods ? paymentMethods.some((pm: any) => pm?.id === currentId) : false;
    if (hasMethods && (!currentId || !stillExists)) {
      setPaymentMethod(paymentMethods[paymentMethods.length - 1]);
    }
  }, [paymentMethods, paymentMethod, setPaymentMethod])
 
  // Update endDate whenever nHours changes (but not when manually selecting time blocks)
  useEffect(() => {
    if (nHours > 0 && startDate && activeBookingDetailStep !== 4) {
      const newEndDate = moment(startDate).add(nHours, 'hours').format('YYYY-MM-DDTHH:mm');
      updateEndDate(newEndDate);
    }
  }, [nHours, startDate, activeBookingDetailStep]);

  useEffect(() => {
    if (currentStep !== 2) {
      setActiveBookingDetailStep(null);
    }
  }, [currentStep]);

  const handleNavigateFromConfirmationToEdit = useCallback((step: number) => {
    setIsEditing(true);
    if (step === 1) {
      updateCurrentStep(excludesSpace ? 2 : 1);
    } else if (step >= 2 && step <= 4) {
      updateCurrentStep(2);
      setBookingActiveSection(step);
      setActiveBookingDetailStep(step);
    } else if (step === 5) {
      updateCurrentStep(3);
    } else if (step === 6) {
      updateCurrentStep(excludesSpace ? 2 : 4);
    } else if (step === 7) {
      updateCurrentStep(5);
    }
  }, [excludesSpace]);

  const handleInviteTeamMember = useCallback((member: Omit<TeamMemberSchema, 'id'>) => {
    const normalizedEmail = member.email.trim().toLowerCase();
    if (
      availableTeamMembers.some(
        (existing) => existing.email.trim().toLowerCase() === normalizedEmail
      )
    ) {
      return;
    }
    const { teamMemberMaxCapacity } = getResourceCapacityLimits(resourceDetail?.resource_options);
    if (teamMemberMaxCapacity != null && selectedTeamMemberIds.length >= teamMemberMaxCapacity) {
      return;
    }
    const newMember: TeamMemberSchema = {
      ...member,
      id: `team-${Date.now()}`,
    };
    setAvailableTeamMembers((prev) => [...prev, newMember]);
    setSelectedTeamMemberIds((prev) => [...prev, newMember.id]);
  }, [availableTeamMembers, resourceDetail?.resource_options, selectedTeamMemberIds.length]);

  const resourceCapacityLimits = useMemo(
    () => getResourceCapacityLimits(resourceDetail?.resource_options),
    [resourceDetail?.resource_options]
  );

  const selectedTeamMembers = useMemo(
    () => availableTeamMembers.filter((member) => selectedTeamMemberIds.includes(member.id)),
    [availableTeamMembers, selectedTeamMemberIds]
  );

  const handleBookingStepToggle = useCallback((step: number) => {
    setBookingActiveSection(step);
    setActiveBookingDetailStep((prev) => (prev === step ? null : step));
  }, []);

  const datesForTimeValidation = useMemo(
    () => (selectedDays.length > 0 ? selectedDays : hasSelectedDate ? [selectedDay] : []),
    [selectedDays, hasSelectedDate, selectedDay]
  );

  const timeSelectionError = useMemo(() => {
    if (!resourceDetail || !nHours || datesForTimeValidation.length === 0) {
      return null;
    }

    if (!availableTimeBlocks?.length) {
      return 'No available time slots for the selected date. The kitchen may be closed or fully booked for the hours you need.';
    }

    if (!selectedBlock) {
      return null;
    }

    if (datesForTimeValidation.length <= 1) {
      const matchesAnchor = availableTimeBlocks.some((block) =>
        block.start.format('HH:mm') === selectedBlock.start.format('HH:mm') &&
        block.end.format('HH:mm') === selectedBlock.end.format('HH:mm')
      );
      if (!matchesAnchor) {
        return 'The selected time is no longer available. Please choose another slot.';
      }
      return null;
    }

    const issues = getDatesWithTimeBlockIssues(
      datesForTimeValidation,
      nHours,
      resourceDetail,
      availabilityBookings,
      selectedBlock
    );
    return formatMultiDateTimeBlockError(issues, selectedBlock);
  }, [
    availableTimeBlocks,
    datesForTimeValidation,
    nHours,
    resourceDetail,
    selectedBlock,
    availabilityBookings,
  ]);

  const bookingPhaseComplete =
    bookingSelections.length > 0 ||
    (
      Boolean(nHours && nHours > 0) &&
      hasSelectedDate &&
      Boolean(selectedBlock) &&
      Boolean(startDate && endDate && moment(endDate).isAfter(moment(startDate))) &&
      !timeSelectionError
    );

  const handleAddHoursDateTime = useCallback(() => {
    const hasValidWindow = startDate && endDate && moment(endDate).isAfter(moment(startDate));
    if (!hasValidWindow || nHours <= 0 || selectedDays.length === 0) {
      updateCurrentStep(2);
      setBookingActiveSection(2);
      setActiveBookingDetailStep(2);
      return;
    }

    const nextStart = moment(startDate).format("HH:mm");
    const nextEnd = moment(endDate).format("HH:mm");
    const nextSelections: BookingSelection[] = selectedDays.map((date) => ({
      id: `${moment(date).format("YYYY-MM-DD")}-${nextStart}-${nextEnd}`,
      date: moment(date).format("YYYY-MM-DD"),
      startTime: nextStart,
      endTime: nextEnd,
      totalHours: nHours,
    }));

    setBookingSelections((prev) => {
      const map = new Map<string, BookingSelection>();
      [...prev, ...nextSelections].forEach((selection) => map.set(selection.id, selection));
      return Array.from(map.values()).sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    });

    updateNHours(0);
    const resetStart = getNext30MinuteInterval();
    updateStartDate(resetStart);
    updateEndDate(resetStart);
    setSelectedDays([]);
    setHasSelectedDate(false);
    setSelectedBlock(undefined);
    updateCurrentStep(2);
    setBookingActiveSection(2);
    setActiveBookingDetailStep(2);
  }, [endDate, nHours, selectedDays, startDate]);

  const isBookingDetailViewOpen = currentStep === 2 && activeBookingDetailStep !== null;

  if (handleClose && isOpen) {
    handleClose();
  }
  return (
    <div className="contain">
      {/* <div className="flex flex-col items-center font-open-sans text-inherit p-6 col-span-1 rounded-md justify-evenly"> */}
      <div className="flex flex-col justify-start items-center h-full min-h-0">
        <span
          onClick={handleClose}
          className="ml-auto w-full flex cursor-pointer mb-2"
        >
          <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[20px] w-[20px] ml-auto" />
        </span>

        <div className="flex flex-row h-[4px] border-0 w-full my-4 mx-auto justify-start gap-2">
          {
            Array(BOOKING_PROGRESS_TOTAL(excludesSpace))
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className={`h-[4px] border-0 flex-1 rounded-full ${
                    i < getBookingProgressIndex(currentStep, excludesSpace) ? "bg-primaryDark-400" : "bg-gray-200"
                  }`}
                />
              )
            )
          }
        </div>

        <span className="text-black font-semibold text-[18px] sm:text-[20px] font-raleway text-left w-full mt-2 mb-1">
          {(currentStep === 1) ? (<div className="flex flex-row my-auto font-bold">Select Booking Plan</div>) : excludesSpace && inventoryOnlyItem ? (
            <>Book <span className="font-bold">{inventoryOnlyItem.item_name}</span></>
          ) : (<>Book <span className="font-bold">{resourceDetail.name}</span></>)}
        </span>
        <span className="text-left text-[13px] sm:text-[14px] my-2 font-open-sans font-normal text-[#4D4D4D] w-full mb-4">
          {
            currentStep === 6 ? "Confirm your booking details." : ((currentStep === 1 || (plan?.plan_recurrence === 'daily' && plan?.plan_max_hours)) ? "" : excludesSpace ? "Select when you need this item." : "Fill out the details below to book your kitchen time.")
          }
        </span>

        {currentStep === 2 ? (
          <div className="flex flex-col w-full mx-auto gap-3">
            {!activeBookingDetailStep ? (
              <BookingDateTimeOverview
                activeRowStep={activeBookingDetailStep ?? bookingActiveSection}
                nHours={nHours}
                hasSelectedDate={hasSelectedDate}
                selectedDay={selectedDay}
                selectedDatesCount={selectedDays.length}
                startDate={startDate}
                endDate={endDate}
                selectedBlock={selectedBlock}
                onStepSelect={handleBookingStepToggle}
                onAddDateTimeSelection={handleAddHoursDateTime}
              />
            ) : (
              <div className="w-full p-3 border border-[#E9E9E9] rounded-lg bg-white">
                {activeBookingDetailStep === 2 ? (
                  <SelectHoursView
                    view={view}
                    plan={plan}
                    nHours={nHours}
                    updateNHours={updateNHours}
                    resourceDetail={resourceDetail}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    startDate={startDate}
                    endDate={endDate}
                    updateStartDate={updateStartDate}
                    updateEndDate={updateEndDate}
                  />
                ) : activeBookingDetailStep === 3 ? (
                  <SelectDateView
                    view={view}
                    currentMember={currentMember}
                    nHours={nHours}
                    updateNHours={updateNHours}
                    resourceDetail={resourceDetail}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    selectedDay={selectedDay}
                    selectedDays={selectedDays}
                    setSelectedDay={handleSelectedDayChange}
                    startDate={startDate}
                    endDate={endDate}
                    setBlocks={setAvailableTimeBlocks}
                    handleConfirmBooking={handleConfirmBooking}
                    bookingRecurrence={bookingRecurrence}
                    setBookingRecurrence={handleBookingRecurrenceChange}
                    endRecurrence={endRecurrence}
                    setEndRecurrence={handleEndRecurrenceChange}
                    onClearSelectedDays={handleClearSelectedDays}
                    dateSelectionInitiated={hasInitiatedCalendarSelection}
                  />
                ) : (
                  <SelectTimeView
                    nHours={nHours}
                    resourceDetail={resourceDetail}
                    selectedPlan={plan}
                    selectedDay={selectedDay}
                    selectedDays={selectedDays}
                    availableTimeBlocks={availableTimeBlocks}
                    selectedBlock={selectedBlock}
                    setSelectedBlock={setSelectedBlock}
                    updateStartDate={updateStartDate}
                    updateEndDate={updateEndDate}
                    timeSelectionError={timeSelectionError}
                  />
                )}
              </div>
            )}
          </div>
        ) : currentStep === 1 ? (
          <SelectBookingPlanView 
            plan={plan} 
            currentMember={currentMember}
            setPlan={setPlan} 
            plans={plans? plans:[]} 
            resourceDetail={resourceDetail} 
            isChangingPlan={isChangingPlan} 
            setIsChangingPlan={setIsChangingPlan} 
          />
        ) : currentStep === 3 ? (
          <AddTeamMembersView
            availableTeamMembers={availableTeamMembers}
            selectedTeamMemberIds={selectedTeamMemberIds}
            setSelectedTeamMemberIds={setSelectedTeamMemberIds}
            onInviteMember={handleInviteTeamMember}
            isMobile
            maxCapacity={resourceCapacityLimits.teamMemberMaxCapacity}
            totalCapacity={resourceCapacityLimits.totalCapacity}
          />
        ) : currentStep === 4 ? (
          <SelectInventoryView
            availableItems={availableInventoryItems}
            orderInventory={orderInventory}
            setOrderInventory={setOrderInventory}
            bookingSelections={allBookingSelections}
            resourceName={resourceDetail.name || ''}
            resourceBookings={resourceBookings}
          />
        ) : currentStep === 5 ? (
          <AdditionalNotesView
            setAdditionalNotes = {setAdditionalNotes}
          />
        ) : currentStep === 6 ? (
          <FinalSubmissionView
            view={view}
            nHours={nHours}
            resourceDetail={resourceDetail}
            currentMember={currentMember}
            setCurrentDate={setCurrentDate}
            startDate={startDate}
            selectedDay={selectedDay}
            endDate={endDate}
            updateCurrentStep={handleNavigateFromConfirmationToEdit}
            setIsEditing={setIsEditing}
            plan={plan?plan:{} as ResourcePlanSchema}
            additionalNotes={additionalNotes}
            selectedTeamMembers={selectedTeamMembers}
            orderInventory={orderInventory}
            bookingSelections={allBookingSelections}
            paymentMethods={paymentMethods}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            selectedBlock={selectedBlock}
            excludesSpace={excludesSpace}
          />
        ) : (
          <></>
        )}

        {/* AdditionalNotesView */}
        {/* PaymentsView */}

        <div className="flex flex-col w-full mx-auto mt-4 gap-3">



          <button
            className={`bg-primaryDark-0 py-3 px-4 text-white font-semibold text-[16px] sm:text-[18px] font-raleway hover:bg-primaryDark-300 border-0 w-full rounded-lg transition-colors touch-manipulation ${
              currentStep === 6 ? "hidden" : "block"
            }`}
            onClick={() => {
              if (isBookingDetailViewOpen) {
                setActiveBookingDetailStep(null);
                return;
              }
              if (isEditing && currentStep > 2) {
                updateCurrentStep(6);
              } else if (isChangingPlan && currentStep === 1) {
                setIsChangingPlan(false);
              } else if (isEditing && currentStep === 1) {
                updateCurrentStep(6);
              } else {
                updateCurrentStep((prev) => getNextBookingStep(prev, excludesSpace));
              }
            }
            }
            disabled={
              !isBookingDetailViewOpen && currentStep === 2 && !bookingPhaseComplete
            }
          >
            {(currentStep === 1 && !isEditing) ? (isChangingPlan ? 'Proceed with Plan' : 'Start Booking' ) : 'Continue'}
          </button>

          <SubmitRequestButton
            isSubmitting={isSubmittingBooking}
            onClick={handleConfirmBooking}
            className={`bg-primaryDark-0 py-3 px-4 text-white text-[16px] sm:text-[18px] font-semibold font-raleway hover:bg-primaryDark-300 border-0 w-full rounded-lg transition-colors touch-manipulation ${currentStep === 6 ? "block" : "hidden"}`}
          />

          <button
            className={`text-primaryDark-0 py-3 px-4 text-[16px] sm:text-[18px] font-semibold font-raleway hover:text-primaryDark-300 border-0 w-full rounded-lg transition-colors touch-manipulation flex items-center justify-center gap-1 ${
              currentStep === 1 || (excludesSpace && currentStep === 2) ? "hidden" : "block"
            }`}
            onClick={() =>
              updateCurrentStep((prev) => getPreviousBookingStep(prev, excludesSpace))
            }
          >
            <ArrowBackIos /> Go Back
          </button>

        </div>






        {/* {isEditing && <button
          className={`bg-primaryDark-0 p-2 text-white text-[18px] font-medium font-raleway hover:bg-back-300 border-0 w-11/12 my-2`}
          onClick={() => {
            setIsEditing(!isEditing)
            updateCurrentStep(7)
          }}
        >
          {isEditing ? "Save Changes" : "Edit Booking"}
        </button>} */}


      </div>
    </div>
  );
};

type BookingDateTimeOverviewProps = {
  /** Sub-step 2 = hours, 3 = date, 4 = time (for row highlight). */
  activeRowStep: number;
  nHours: number;
  hasSelectedDate: boolean;
  selectedDay: Date;
  selectedDatesCount: number;
  startDate: string;
  endDate: string;
  selectedBlock?: TimeBlock;
  onStepSelect: (step: number) => void;
  onAddDateTimeSelection: () => void;
};

const BookingDateTimeOverview: React.FC<BookingDateTimeOverviewProps> = ({
  activeRowStep,
  nHours,
  hasSelectedDate,
  selectedDay,
  selectedDatesCount,
  startDate,
  endDate,
  selectedBlock,
  onStepSelect,
  onAddDateTimeSelection,
}) => {
  const hoursLabel = nHours > 0 ? `${nHours} Hour${nHours > 1 ? "s" : ""}` : "Add Hours";
  const dateLabel = hasSelectedDate
    ? selectedDatesCount > 1
      ? `${selectedDatesCount} dates selected`
      : moment(selectedDay).format("dddd, MMMM D, YYYY")
    : "Select Date";
  const timeLabel = selectedBlock ? `${moment(startDate).format("h:mma")} - ${moment(endDate).format("h:mma")}` : "Select Available Time";

  const rows = [
    { step: 2, label: hoursLabel, icon: <HourGlassIcon className="text-[#51A0B5]" />, disabled: false },
    { step: 3, label: dateLabel, icon: <CalendarMonthIcon className="text-[#51A0B5]" />, disabled: !(nHours > 0) },
    { step: 4, label: timeLabel, icon: <AccessTimeIcon className="text-[#51A0B5]" />, disabled: !(nHours > 0 && hasSelectedDate) },
  ];

  return (
    <div className="w-full p-4 bg-[#F8F8F8] rounded-lg">
      <div className="space-y-3">
        {rows.map((row) => (
          <button
            key={row.step}
            type="button"
            onClick={() => onStepSelect(row.step)}
            disabled={row.disabled}
            className={`w-full border-0 rounded-lg px-4 py-4 flex items-center justify-between transition-colors ${
              activeRowStep === row.step ? "bg-white shadow-sm" : "bg-white/80 hover:bg-white"
            } ${row.disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <span className="text-[#000] font-open-sans text-[16px] text-left">{row.label}</span>
            <span className="w-9 h-9 rounded-full bg-[#D9EEF3] flex items-center justify-center">{row.icon}</span>
          </button>
        ))}
      </div>

      {/* <button type="button" onClick={onAddDateTimeSelection} className="mt-4 border-0 bg-transparent p-0 flex items-center text-primaryDark-0">
        <AddCircleOutline className="mr-2 text-[#A1A1A1]" fontSize="small" />
        <span className="font-open-sans text-[16px] font-semibold">Add Hours, Date &amp; Time</span>
      </button> */}
    </div>
  );
};

type SelectHoursViewProps = {
  view: string;
  resourceDetail: ResourceSchema;
  plan: ResourcePlanSchema;
  currentDate: Date;
  setCurrentDate: any;
  startDate: any;
  endDate: any;
  updateStartDate: any;
  updateEndDate: any;
  nHours: number;
  updateNHours: React.Dispatch<React.SetStateAction<number>>;
  handleConfirmBooking?: () => void;
};
const SelectHoursView: React.FC<SelectHoursViewProps> = ({
  // view,
  // resourceDetail,
  nHours,
  updateNHours,
  // currentDate,
  startDate,
  endDate,
  plan,
  // setCurrentDate,
  // updateStartDate,
  updateEndDate,
}) => {

  useEffect(() => {
    if (plan?.plan_recurrence === 'daily' && plan?.plan_max_hours) {
      updateNHours(plan.plan_max_hours);
    }
  }, [plan?.plan_recurrence, plan?.plan_max_hours, updateNHours]);

  return (
    <div className="w-full mx-auto h-full">
      <div className="flex flex-col w-full mt-3 justify-between ">
        {
          plan?.plan_recurrence === 'daily' && plan?.plan_max_hours ? (
            <p className="font-medium text-left text-[14px] font-open-sans">
              <span className="font-semibold text-[17px] font-raleway text-primaryDark-0">{capitalizeString(plan?.plan_name || 'plan')}</span> includes <span className="font-semibold text-[15px] font-raleway text-primaryDark-300">{plan.plan_max_hours} hours</span> per day
            </p>
          ) : (
            <>
              <p className="text-black font-medium text-left text-[16px] font-raleway">
                  How many hours do you need?
              </p>
              <div className="flex flex-row gap-x-5 w-full justify-center my-2 font-bold text-gray-300 border-[#E9E9E9] border-[1px] rounded-md p-1">
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.5"
                  className="bg-inherit my-auto text-left border-0  cursor-pointer text-primary-300 rounded-lg w-full"
                  value={(plan?.plan_recurrence === 'daily' && plan?.plan_max_hours) ? plan.plan_max_hours.toFixed(1) : nHours.toFixed(1)}
                  onChange={(e) => {
                    e.preventDefault();
                    let { value } = e.target;
                    let intE = parseFloat(value);
                    if (intE === 0 || (intE && plan?.plan_recurrence !== 'daily')) {
                      updateNHours(intE);
                    }
                  }}
                  disabled={(plan && plan?.plan_recurrence === 'daily' && plan?.plan_max_hours) ? true : false}
                />

                <div
                  onClick={() => {
                    if (nHours > 0.5) {
                      updateNHours(nHours - 0.5);
                      let newEndDate = moment(endDate);
                      newEndDate.subtract(30, "minutes");
                      updateEndDate(newEndDate.format("YYYY-MM-DDTHH:mm"));
                    }
                  }}
                  className={`flex ${plan?.plan_recurrence === 'daily' && plan?.plan_max_hours ? 'hidden' : 'block'} rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto`}
                >
                  <LeftArrow className=" text-black rounded-full  m-auto" />
                </div>

                <div
                  onClick={() => {
                    if (nHours < 24) {
                      updateNHours(nHours + 0.5);
                      let newEndDate = moment(endDate);
                      newEndDate.add(30, "minutes");
                      updateEndDate(newEndDate.format("YYYY-MM-DDTHH:mm"));
                    }
                  }}
                  className={`flex ${plan?.plan_recurrence === 'daily' && plan?.plan_max_hours ? 'hidden' : 'block'} rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto`}
                >
                  <RightArrow className="text-black rounded-full m-auto" />
                </div>
              </div>
            </>
          )
        }

      </div>
    </div>
  );
};

type SelectDateViewProps = {
  view: string;
  currentMember: MembershipSchema;
  resourceDetail: ResourceSchema;
  currentDate: Date;
  setCurrentDate: any;
  startDate: any;
  endDate: any;
  selectedDay: any;
  selectedDays: Date[];
  setSelectedDay: any;
  nHours: number;
  setBlocks: React.Dispatch<React.SetStateAction<TimeBlock[] | undefined>>;
  updateNHours: React.Dispatch<React.SetStateAction<number>>;
  handleConfirmBooking?: () => void;
  bookingRecurrence: BookingRecurrenceType;
  setBookingRecurrence: (value: BookingRecurrenceType) => void;
  endRecurrence: string;
  setEndRecurrence: (value: string) => void;
  onClearSelectedDays: () => void;
  dateSelectionInitiated: boolean;
};
const SelectDateView: React.FC<SelectDateViewProps> = ({
  view,
  currentMember,
  nHours,
  // updateNHours,
  handleConfirmBooking,
  resourceDetail,
  currentDate,
  selectedDay,
  selectedDays,
  setSelectedDay,
  setBlocks,
  setCurrentDate,
  bookingRecurrence,
  setBookingRecurrence,
  endRecurrence,
  setEndRecurrence,
  onClearSelectedDays,
  dateSelectionInitiated,
}) => {
  // const [] = useState<number>(0)
  // const { user } = useCompleteSignIn();
  // const loggedin_username = user && user?.username ? user.username : "";
  // const bookings = useLoadBookings(loggedin_username).bookings;
  // console.log("bookings ", bookings);
  
  const next = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const prev = () => {
    setCurrentDate(addMonths(currentDate, -1));
  };

  var { bookings: userBookings } = useLoadBookings(currentMember?.uid || "");
  const { resourceBookings } = useLoadResourceBookings(
    resourceDetail?.community_id || "",
    resourceDetail?.id || ""
  );
  const recurrenceControls = getRecurrenceControlsFromSelectedDays(selectedDays);
  const doesNotRepeatLabel =
    recurrenceControls.recurrence === 'once' && selectedDays.length > 1
      ? 'Custom'
      : 'Does not repeat';

  return (
    <div className="w-full flex flex-col">
      <p className="text-black font-medium text-left text-[16px] font-raleway mt-3">
        Choose your date
      </p>
    
      <div className="flex flex-col w-full mx-auto my-2 justify-between p-3 sm:p-4 border-[1px] border-[#E9E9E9] rounded-lg overflow-hidden">
        <div className="flex flex-row mb-3">
          <h2
            data-testid="calendar-month-label"
            className="text-[16px] text-[#4D4D4D] font-raleway font-medium text-center my-auto text-primary-400"
          >
            {format(currentDate, "MMMM yyyy")}
          </h2>

          <div className="flex-row justify-between items-center text-slate-400 my-auto ml-auto">
            <button 
              onClick={prev}
              className="mr-4 rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto"
            >
              <LeftArrow className=" text-black rounded-full  m-auto" />
            </button>
            <button 
              onClick={next}
              className="mr-4 rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto"
            >
                <RightArrow className="text-black rounded-full m-auto" />
            </button>
          </div>
        </div>

        <div
          className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-3 pb-3 border-b border-[#E9E9E9]"
          data-testid="booking-recurrence-controls"
        >
          <span className="text-[11px] text-[#888888] font-open-sans uppercase tracking-wide shrink-0">
            Repeat
          </span>
          <select
            data-testid="booking-recurrence-select"
            aria-label="Repeat booking"
            className="h-10 min-w-[7.5rem] flex-1 max-w-[11rem] rounded-md border border-[#E9E9E9] bg-[#FAFAFA] px-2 text-[13px] font-open-sans text-[#4D4D4D] focus:outline-none focus:border-[#51A0B5]"
            value={bookingRecurrence}
            onChange={(e) => setBookingRecurrence(e.target.value as BookingRecurrenceType)}
          >
            {BOOKING_RECURRENCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value === 'once' ? doesNotRepeatLabel : option.label}
              </option>
            ))}
          </select>
          {bookingRecurrence !== 'once' && (
            <>
              <span className="text-[12px] text-[#888888] font-open-sans shrink-0">until</span>
              <input
                type="date"
                data-testid="end-recurrence-input"
                aria-label="Repeat until"
                className="h-10 min-w-[9rem] flex-1 max-w-[11rem] rounded-md border border-[#E9E9E9] bg-[#FAFAFA] px-2 text-[13px] font-open-sans text-[#4D4D4D] focus:outline-none focus:border-[#51A0B5]"
                value={endRecurrence}
                min={
                  selectedDays.length > 0
                    ? moment(
                        Math.min(...selectedDays.map((d) => moment(d).valueOf()))
                      ).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD')
                }
                onChange={(e) => setEndRecurrence(e.target.value)}
              />
            </>
          )}
          <button
            type="button"
            data-testid="clear-selected-days"
            onClick={onClearSelectedDays}
            className="h-10 px-3 rounded-md border border-[#E9E9E9] bg-white text-[13px] font-open-sans text-[#4D4D4D] hover:bg-[#F7F7F7]"
          >
            Clear dates
          </button>
        </div>

        <div className="flex justify-center items-center w-full">
          <ReservationCalendar
            currentCalendarDate={currentDate}
            userBookings={userBookings || [] as ResourceBookingSchema[]}
            resourceBookings={resourceBookings || [] as ResourceBookingSchema[]}
            setBlocks={setBlocks}
            nHours={nHours}
            selectedDay={selectedDay}
            highlightedDays={selectedDays}
            handleDateChange={setSelectedDay}
            handleConfirmBooking={handleConfirmBooking}
            currentKitchen={resourceDetail}
            bookingRecurrence={bookingRecurrence}
            endRecurrence={endRecurrence}
            dateSelectionInitiated={dateSelectionInitiated}
          />
        </div>
      </div>
    </div>
  );
};

type SelectTimeViewProps = {
  resourceDetail: ResourceSchema;
  selectedDay: any
  selectedDays: Date[];
  nHours: number;
  updateStartDate: any;
  updateEndDate: any;
  availableTimeBlocks: TimeBlock[] | undefined;
  selectedBlock: TimeBlock | undefined
  selectedPlan: ResourcePlanSchema;
  setSelectedBlock: React.Dispatch<React.SetStateAction<TimeBlock | undefined>>;
  timeSelectionError: string | null;
};

const SelectTimeView: React.FC<SelectTimeViewProps> = ({
  updateEndDate,
  updateStartDate,
  selectedDay,
  selectedDays,
  availableTimeBlocks,
  selectedBlock,
  setSelectedBlock,
  resourceDetail,
  selectedPlan,
  timeSelectionError,
}) => {
  
  return (
    <TimesView
      selectedPlan={selectedPlan}
      selectedDay={selectedDay}
      selectedDatesCount={selectedDays.length}
      updateEndTime={updateEndDate}
      updateStartTime={updateStartDate}
      availableTimeBlocks={availableTimeBlocks}
      selectedBlock={selectedBlock}
      setSelectedBlock={setSelectedBlock}
      resourceDetail={resourceDetail}
      timeSelectionError={timeSelectionError}
    />
  );
};


type SelectBookingPlanViewProp = {
  resourceDetail: ResourceSchema;
  plans: ResourcePlanSchema[];
  plan: ResourcePlanSchema
  currentMember: MembershipSchema
  setPlan: React.Dispatch<React.SetStateAction<ResourcePlanSchema>>;
  isChangingPlan: boolean;
  setIsChangingPlan: React.Dispatch<React.SetStateAction<boolean>>;
};

export const SelectBookingPlanView: React.FC<SelectBookingPlanViewProp> = ({ resourceDetail, plan, plans, currentMember, setPlan, isChangingPlan, setIsChangingPlan }) => {
  // const handlePlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   const selectedMethod = plans.find((plan:any) => plan.plan_id === e.target.value);
  //   setPlan(selectedMethod as ResourcePlanSchema);
  // };

  // const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewPlanDetails = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleContinueToBooking = () => {
    setIsModalOpen(false);
    // Add any additional logic for continuing to booking
  };

  const currentMemberBookingPlanId = getMemberBookingPlanIdForResource(currentMember, plan?.resource_id || '', plans)
  const currentMemberBookingBalance = getMemberBookingBalanceForResource(currentMember, plan?.resource_id || '', plan?.plan_type)


  return (
    <div className="flex flex-col w-11/12 h-full text-left mx-auto">
      {
        (isChangingPlan) ? (
          <SelectBookingPlanForm plan={plan} plans={plans} setPlan={setPlan} handleViewPlanDetails={handleViewPlanDetails}/>
        ) : (
          <>
            {/* Header with plan selection and view details link */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex flex-row my-auto">
                <p className="font-raleway text-[16px] text-[#808080] my-auto">
                  You have selected <span className="font-bold text-[#013847]">{plan?.plan_name}</span>
                </p>
                {/* Change plan option */}
                <div className="my-auto ml-2">
                  <span 
                    className="text-primaryDark-300 text-[12px] cursor-pointer hover:text-primaryDark-400 transition-colors"
                    onClick={() => setIsChangingPlan(true)}
                  >
                    Change Plan
                  </span>
                </div>
              </div>
              <span 
                className="text-[#808080] font-raleway text-[14px] cursor-pointer font-medium underline hover:text-[#51A0B5] transition-colors"
                onClick={handleViewPlanDetails}
              >
                View Plan Details
              </span>
            </div>

            {/* plan details box */}
            <div className="bg-primaryDark-400 rounded-lg p-6 text-white">
              <div className="space-y-4">
                {/* Standard Rate */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-white">Standard Rate</span>
                  </div>
                  <span className="font-semibold">${plan?.plan_rate ? styleThousandCommas(plan.plan_rate) : '10'} / {planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</span>
                </div>

                {/* Off-Peak Rate */}
                {
                ((plan?.plan_recurrence === "hourly" || plan?.plan_recurrence === "daily") && plan?.pricing_type === "dynamic") ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-white">Off-Peak Rate</span>
                  </div>
                  <span className="font-semibold">${plan?.off_peak_rate ? styleThousandCommas(plan.off_peak_rate) : '0'} / {planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</span>
                </div>) : (<></>)
                }

                {/* Peak Rate */}
                {
                  ((plan?.plan_recurrence === "hourly" || plan?.plan_recurrence === "daily") && plan?.pricing_type === "dynamic") ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center mr-3">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white">Peak Rate</span>
                    </div>
                    <span className="font-semibold">${plan?.peak_rate ? styleThousandCommas(plan.peak_rate) : '0'} / {planRecurrenceMap[plan?.plan_recurrence as keyof typeof planRecurrenceMap]}</span>
                  </div>
                  ) : (<></>)
                }

                {/* Max Hours */}
                {(plan?.plan_max_hours) ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center mr-3">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white">Max Duration</span>
                    </div>
                    <span className="font-semibold">
                      {formatMaxHours(plan)} 
                      {
                        (plan?.plan_max_hours && ["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) ? (
                          <span className="text-[12px] text-gray-100 font-open-sans">&nbsp;({plan?.plan_max_hours} hours)</span>
                        ) : (<></>)
                      }
                    </span>
                  </div>) : (<></>)
                }

                {/* Overage */}
                {
                  (plan?.plan_max_hours && plan?.overage_rate) ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center mr-3">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-white">Overage Rate</span>
                      </div>
                      <span className="font-semibold">${plan?.overage_rate ? styleThousandCommas(plan.overage_rate) : '0'} / hour</span>
                    </div>
                  ) : (<></>)
                }

                {/* Booking Balance */}
                {
                  (currentMemberBookingBalance && currentMemberBookingPlanId === plan?.plan_id) ? (
                    <>
                    <hr className="border-white/50 my-2" />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {/* <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center mr-3">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div> */}
                        <span className="text-white font-semibold">Booking Time Remaining</span>
                      </div>
                      <span className="font-bold">{currentMemberBookingBalance ? styleThousandCommas(currentMemberBookingBalance) : '0'} hours</span>
                    </div>
                    </>
                  ) : (<></>)
                }
              </div>
            </div>

            
          </>
        )
      }

      {/* Modal */}
      <ViewPlanDetailsModal 
        isOpen={isModalOpen}
        onClose={handleModalClose}
        plan={plan}
        resourceDetail={resourceDetail}
        onContinue={handleContinueToBooking}
      />
    </div>
  );
};


type SelectBookingPlanFormProps = {
  plan: ResourcePlanSchema;
  plans: ResourcePlanSchema[];
  setPlan: React.Dispatch<React.SetStateAction<ResourcePlanSchema>>;
  handleViewPlanDetails: () => void;
};
const SelectBookingPlanForm: React.FC<SelectBookingPlanFormProps> = ({ plan, plans, setPlan, handleViewPlanDetails }) => {
  const handlePlanSelect = (planId: string) => {
    const selectedPlan = plans.find((p: ResourcePlanSchema) => p.plan_id === planId);
    if (selectedPlan) {
      setPlan(selectedPlan);
    }
  };

  // const formatPrice = (rate: number, recurrence?: string) => {
  //   if (!rate) return '';
  //   const recurrenceText = recurrence === 'monthly' ? '/Month' : recurrence === 'weekly' ? '/Week' : recurrence === 'yearly' ? '/Year' : '';
  //   return `$${rate}${recurrenceText}`;
  // };

  const formatMaxHours = (plan: ResourcePlanSchema) => {
    // if (plan){
    //   {(["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
    //     ? Math.floor((plan.plan_max_hours || 0) / 24) 
    //     : (plan.plan_max_hours || 0)
    //   }
    // }
    const maxHours = (["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
      ? Math.floor((plan.plan_max_hours || 0) / 24) 
      : (plan.plan_max_hours || 0)
    return maxHours ? `${maxHours} ${["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any) ? "Max Days" : "Max Hours"}` : '';
  };

  return (
    <div className="flex flex-col w-full">
      {/* <p className="font-raleway text-[16px] text-black mb-4">Select Plan</p> */}
      
      <div className="space-y-3">
        {plans.map((p: ResourcePlanSchema, index) => (
          <div 
            key={p.plan_id || index}
            className={`border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              plan?.plan_id === p.plan_id 
                ? 'border-primaryDark-400 bg-primaryDark-400/10' 
                : 'hover:border-gray-300'
            }`}
            onClick={() => handlePlanSelect(p.plan_id || '')}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                {/* Radio Button */}
                <div className="flex items-center mt-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    plan?.plan_id === p.plan_id 
                      ? 'border-primaryDark-400 bg-primaryDark-400' 
                      : 'border-gray-300'
                  }`}>
                    {plan?.plan_id === p.plan_id && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Plan Details */}
                <div className="flex-1">
                  <h3 className="text-[18px] font-semibold font-raleway text-black mb-1">
                    {p.plan_name || ''}
                  </h3>
                  <p className="text-[#4D4D4D] text-[16px] font-open-sans mb-3">
                  {(!p.plan_description || p.plan_description?.length < 50) ? p.plan_description : `${p.plan_description?.slice(0, 50)}...`}
                  </p>
                  
                  {/* Features */}
                  {p.features && p.features.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-raleway text-gray-500 mb-2">Plan Features</p>
                      <div className="space-y-1">
                        {p.features.map((feature: string, featureIndex: number) => (
                          <div key={featureIndex} className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded-full bg-primaryDark-400/10 flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-primaryDark-300" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-[14px] text-gray-700 font-open-sans font-normal">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="text-right ml-4">
                  <div className="text-[18px] font-semibold font-raleway text-black">
                    ${p.plan_rate && styleThousandCommas(p.plan_rate)} / {planRecurrenceMap[p.plan_recurrence as keyof typeof planRecurrenceMap]}
                  </div>
                  <div className="text-[16px] text-[#4D4D4D] font-open-sans font-light">
                    {formatMaxHours(p)}
                  </div>

                  <span 
                    className="text-primaryDark-300 font-raleway text-[12px] cursor-pointer hover:text-primaryDark-400 transition-colors"
                    onClick={handleViewPlanDetails}
                  >
                    Plan Details
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confirm Button */}
      {/* <button 
        onClick={() => setIsChangingPlan(false)}
        className={`mt-6 w-full py-3 px-4 rounded-lg font-semibold  transition-colors duration-200 ${
          plan?.plan_id 
            ? 'bg-white border-[1px] border-[#E9E9E9] hover:bg-primaryDark-300 hover:text-white text-gray-500' 
            : 'bg-gray-300 cursor-not-allowed text-white'
        }`}
        disabled={!plan?.plan_id}
      >
        Confirm Plan Selection
      </button> */}
    </div>
  )
};

// type SelectPlanViewProp = {
//   resourceDetail: ResourceSchema;
//   plans: PlanSchema[];
//   plan: PlanSchema | undefined
//   setPlan: React.Dispatch<React.SetStateAction<PlanSchema | undefined>>;
// };

// const SelectPlanView: React.FC<SelectPlanViewProp> = ({ plan, plans, setPlan }) => {
//   const handlePlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
//     const selectedMethod = plans.find((plan:any) => plan.plan_id === e.target.value);
//     setPlan(selectedMethod);
//   };

//   const [isChangePlan, setIsChangePlan] = useState(false);
//   return (
//     <div className="flex flex-col w-11/12 h-full text-left mx-auto">
//       {
//         (isChangePlan) ? (
//           <>
//             <p className="font-raleway text-[16px] text-black">Choose Plan</p>
//             <select value={plan?.plan_id} onChange={handlePlanChange} className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#808080] font-open-sans">
//               <option value="">Choose a plan</option>
//               {plans.map((p: PlanSchema) => (
//                 <option key={p.plan_id} value={p.plan_id}>
//                   {p.plan_name}
//                 </option>
//               ))}
//             </select>
//           </>
//         ) : (
//           <>
//             <p className="font-raleway text-[16px] text-black">
//               Current Plan &nbsp;
//               <span 
//                 className="text-primaryDark-300 text-[14px] cursor-pointer"
//                 onClick={() => setIsChangePlan(true)}
//               >
//                 Change
//               </span>
//             </p>
//             <p className="font-raleway text-[16px] text-[#808080]">{plan?.plan_name} - ${(plan?.plan_rate) && styleThousandCommas(plan?.plan_rate)} ({plan?.plan_recurrence})</p>
//           </>
//         )
//       }
//     </div>
//   );
// };
type AdditionalNotesViewProps = {
  setAdditionalNotes: React.Dispatch<React.SetStateAction<string>>;
};

const AdditionalNotesView: React.FC<AdditionalNotesViewProps> = ({
  setAdditionalNotes
}) => {
  return (
    <div className="flex flex-col w-11/12 h-full text-left mx-auto">
      <p className="font-raleway text-[16px] text-black">Additional Notes</p>
      <textarea onChange={(e) => setAdditionalNotes(e.target.value)}
        placeholder="Additional Notes"
        className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#808080] font-open-sans"
      />
    </div>
  );
};

type SelectInventoryViewProps = {
  availableItems: InventorySchema[];
  orderInventory: OrderItemSchema[];
  setOrderInventory: React.Dispatch<React.SetStateAction<OrderItemSchema[]>>;
  bookingSelections: BookingSelection[];
  resourceName: string;
  resourceBookings?: InventoryStockBooking[];
};

const getInventoryItemKey = (item: InventorySchema) => item.id || item.item_name;

const getInventoryOrderItemId = (item: InventorySchema, selectionId: string) =>
  `${selectionId}-${getInventoryItemKey(item)}`;

const matchesInventoryCatalogItem = (orderItem: OrderItemSchema, item: InventorySchema) =>
  (orderItem.inventory_id && orderItem.inventory_id === item.id) ||
  orderItem.item_name === item.item_name;

const getInventoryDescription = (item: InventorySchema) =>
  item.category
    ? `${item.category.charAt(0).toUpperCase()}${item.category.slice(1)} item available for your session`
    : 'Available inventory item for your session';

type AggregatedInventorySummaryItem = {
  key: string;
  item_name: string;
  unit_price?: number;
  measureUnit?: string;
  quantityPerBooking: number;
  bookingCount: number;
  totalQuantity: number;
};

const getOrderInventoryForSelection = (orderInventory: OrderItemSchema[], selectionId: string) =>
  orderInventory.filter((item) => item.id.startsWith(`${selectionId}-`));

const expandOrderInventoryForSelections = (
  orderInventory: OrderItemSchema[],
  bookingSelections: BookingSelection[],
  availableItems: InventorySchema[] = [],
  resourceName = ''
): OrderItemSchema[] => {
  if (bookingSelections.length === 0) {
    return orderInventory;
  }

  const templateItems = new Map<string, OrderItemSchema>();
  orderInventory.forEach((orderItem) => {
    const key = orderItem.inventory_id || orderItem.item_name;
    if (!templateItems.has(key)) {
      templateItems.set(key, orderItem);
    }
  });

  if (templateItems.size === 0) {
    return orderInventory;
  }

  return Array.from(templateItems.values()).flatMap((template) => {
    const catalogItem = availableItems.find(
      (item) => getInventoryItemKey(item) === (template.inventory_id || template.item_name)
    );
    const baseItem = catalogItem ?? template;
    const inventoryStub = {
      id: template.inventory_id || baseItem.id,
      item_name: template.item_name,
    } as InventorySchema;

    return bookingSelections.map((selection) => ({
      ...baseItem,
      id: getInventoryOrderItemId(inventoryStub, selection.id),
      inventory_id: template.inventory_id || catalogItem?.id || '',
      total_quantity: template.total_quantity,
      available_quantity: template.available_quantity ?? catalogItem?.available_quantity,
      resource_name: resourceName || template.resource_name,
      item_name: template.item_name,
      category: template.category,
      resource_type: template.resource_type,
    }));
  });
};

const buildAggregatedInventorySummary = (
  orderInventory: OrderItemSchema[]
): AggregatedInventorySummaryItem[] => {
  const summary = new Map<string, AggregatedInventorySummaryItem>();
  orderInventory.forEach((orderItem) => {
    const key = orderItem.inventory_id || orderItem.item_name;
    const existing = summary.get(key);
    if (existing) {
      existing.totalQuantity += orderItem.total_quantity;
      existing.bookingCount += 1;
    } else {
      summary.set(key, {
        key,
        item_name: orderItem.item_name,
        unit_price: orderItem.unit_price,
        measureUnit: orderItem.measure_unit,
        quantityPerBooking: orderItem.total_quantity,
        bookingCount: 1,
        totalQuantity: orderItem.total_quantity,
      });
    }
  });
  return Array.from(summary.values());
};

const AggregatedInventoryLabel: React.FC<{ item: AggregatedInventorySummaryItem }> = ({ item }) => (
  <>
    <span>
      {/* {item.item_name} x{item.quantityPerBooking} */}
      x{item.quantityPerBooking} {item.item_name} {item.unit_price ? `@ $${item.unit_price} / ${item.measureUnit}` : ''}
    </span>
    {item.bookingCount > 1 && (
      <span className="text-[#999999] font-open-sans text-[11px] font-normal">
        {' '}
        ({item.bookingCount} bookings)
      </span>
    )}
  </>
);

const SelectInventoryView: React.FC<SelectInventoryViewProps> = ({
  availableItems,
  orderInventory,
  setOrderInventory,
  bookingSelections,
  resourceName,
  resourceBookings,
}) => {
  const sessionWindows = useMemo(
    () => getInventorySessionWindows(bookingSelections),
    [bookingSelections]
  );

  const getItemMaxQuantity = useCallback(
    (item: InventorySchema) =>
      getInventoryItemRemainingForSessions(item, resourceBookings, sessionWindows),
    [resourceBookings, sessionWindows]
  );
  const buildOrderItem = useCallback(
    (item: InventorySchema, quantity: number, selection: BookingSelection): OrderItemSchema => ({
      ...item,
      id: getInventoryOrderItemId(item, selection.id),
      inventory_id: item.id || '',
      total_quantity: quantity,
      available_quantity: item.available_quantity,
      resource_name: resourceName,
      item_name: item.item_name,
      category: item.category,
      resource_type: item.resource_type,
    }),
    [resourceName]
  );

  const expandInventoryForAllBookings = useCallback(
    (item: InventorySchema, quantity: number): OrderItemSchema[] =>
      bookingSelections.map((selection) => buildOrderItem(item, quantity, selection)),
    [bookingSelections, buildOrderItem]
  );

  const isInventoryItemSelected = (item: InventorySchema) =>
    bookingSelections.length > 0 &&
    bookingSelections.every((selection) =>
      orderInventory.some(
        (orderItem) => orderItem.id === getInventoryOrderItemId(item, selection.id)
      )
    );

  const getInventoryQuantity = (item: InventorySchema) => {
    const firstSelection = bookingSelections[0];
    if (!firstSelection) return 1;
    const found = orderInventory.find(
      (orderItem) => orderItem.id === getInventoryOrderItemId(item, firstSelection.id)
    );
    return found?.total_quantity ?? 1;
  };

  const toggleInventoryItem = (item: InventorySchema, checked: boolean) => {
    if (checked && getItemMaxQuantity(item) === 0) return;

    if (checked) {
      setOrderInventory((prev) => {
        if (isInventoryItemSelected(item)) return prev;
        return [...prev, ...expandInventoryForAllBookings(item, 1)];
      });
    } else {
      setOrderInventory((prev) =>
        prev.filter((orderItem) => !matchesInventoryCatalogItem(orderItem, item))
      );
    }
  };

  const setInventoryQuantity = (item: InventorySchema, nextQty: number) => {
    const maxQty = getItemMaxQuantity(item);
    const newQty = Math.max(0, Math.min(maxQty, Math.floor(nextQty)));

    if (newQty <= 0) {
      toggleInventoryItem(item, false);
      return;
    }

    if (!isInventoryItemSelected(item)) {
      setOrderInventory((prev) => [...prev, ...expandInventoryForAllBookings(item, newQty)]);
      return;
    }

    setOrderInventory((prev) =>
      prev.map((orderItem) =>
        matchesInventoryCatalogItem(orderItem, item)
          ? { ...orderItem, total_quantity: newQty }
          : orderItem
      )
    );
  };

  const updateInventoryQuantity = (item: InventorySchema, delta: number) => {
    setInventoryQuantity(item, getInventoryQuantity(item) + delta);
  };

  const handleQuantityInputChange = (item: InventorySchema, value: string) => {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      setInventoryQuantity(item, parsed);
    }
  };

  const stopInventoryCardToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const singleSessionDateLabel =
    bookingSelections.length === 1
      ? formatLongBookingDateLabel(bookingSelections[0].date)
      : null;

  return (
    <div className="flex flex-col w-full text-left mx-auto gap-4">
      {/* {bookingSelections.length > 0 && (
        <div className="flex flex-row gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {bookingSelections.map((selection) => (
            <div
              key={selection.id}
              className="flex-shrink-0 flex flex-col items-start p-3 rounded-lg border min-w-[140px] bg-primaryDark-400 border-primaryDark-400 text-white"
            >
              <CalendarMonthIcon className="w-4 h-4 mb-1 text-white" />
              <span className="font-open-sans text-[13px] font-semibold">
                {moment(selection.date).format('DD/MM/YYYY')}
              </span>
              <span className="font-open-sans text-[12px] text-white/90">
                {moment(selection.startTime, 'HH:mm').format('h:mma')} - {moment(selection.endTime, 'HH:mm').format('h:mma')}
              </span>
            </div>
          ))}
        </div>
      )} */}

      <p className="font-raleway text-[16px] text-black">
        {singleSessionDateLabel ? (
          <>
            Select the items you&apos;ll need for your session on <b>{singleSessionDateLabel}</b>.
          </>
        ) : bookingSelections.length > 1 ? (
          <>
            Select the items you&apos;ll need for each of your <b>{bookingSelections.length} sessions</b>.
            {' '}Your selections apply to every booking.
          </>
        ) : (
          <>Select the items you&apos;ll need for your session.</>
        )}
      </p>

      {availableItems.length > 0 ? (
        <div className="flex flex-col gap-3">
          {availableItems.map((item, index) => {
            const selected = isInventoryItemSelected(item);
            const quantity = getInventoryQuantity(item);
            const maxQty = getItemMaxQuantity(item);
            const isOutOfStock = maxQty === 0;
            const atMax = maxQty != null && quantity >= maxQty;
            const atMin = quantity <= 1;
            const remainingQuantity = selected ? quantity : 0;

            return (
              <div
                key={getInventoryItemKey(item) || index}
                onClick={isOutOfStock ? undefined : () => toggleInventoryItem(item, !selected)}
                aria-disabled={isOutOfStock}
                className={`relative flex flex-row gap-3 p-3 border rounded-lg transition-all duration-200 ${
                  isOutOfStock
                    ? inventoryOutOfStockCardClassName
                    : selected
                      ? 'border-primaryDark-300 bg-primaryDark-400/10 cursor-pointer'
                      : 'border-[#E9E9E9] bg-white hover:bg-gray-50 cursor-pointer'
                }`}
              >
                {isOutOfStock ? (
                  <InventoryOutOfStockCheckboxSlot positionClassName="absolute top-3 right-3" />
                ) : (
                  <label
                    className="absolute top-3 right-3 cursor-pointer"
                    onClick={stopInventoryCardToggle}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => toggleInventoryItem(item, e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        selected
                          ? 'border-primaryDark-300 bg-primaryDark-300'
                          : 'border-[#D9D8D8] bg-white'
                      }`}
                    >
                      {selected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </label>
                )}
                {isOutOfStock ? (
                  <InventoryOutOfStockImage
                    imageUrl={item.image_url}
                    itemName={item.item_name}
                    size="mobile"
                  />
                ) : item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.item_name}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-[#eee] flex-shrink-0" />
                )}
                <div className="flex flex-col flex-1 min-w-0 pr-6">
                  <p className={`font-raleway font-bold text-[15px] ${isOutOfStock ? 'text-[#6B6B6B]' : 'text-black'}`}>
                    {item.item_name}
                  </p>
                  <p className={`font-open-sans text-[13px] mt-0.5 line-clamp-2 ${isOutOfStock ? 'text-[#9A9A9A]' : 'text-[#4D4D4D]'}`}>
                    {isOutOfStock ? inventoryOutOfStockDescription : getInventoryDescription(item)}
                  </p>
                  {isOutOfStock && <InventoryOutOfStockBadge size="mobile" />}
                  {selected && !isOutOfStock && (
                    <div className="flex flex-row items-center mt-3 gap-2 flex-wrap">
                      {formatInventoryUnitPriceLabel(item.unit_price, item.measure_unit) && (
                        <span
                          data-testid="inventory-unit-price-label"
                          className="font-open-sans text-[14px] font-medium text-primaryDark-0/50"
                        >
                          {formatInventoryUnitPriceLabel(item.unit_price, item.measure_unit)}
                        </span>
                      )}
                      <div
                        className="flex flex-row items-center border border-[#E9E9E9] rounded-lg bg-white overflow-hidden"
                        onClick={stopInventoryCardToggle}
                      >
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateInventoryQuantity(item, -1);
                          }}
                          disabled={atMin}
                          className="flex border-[#E9E9E9] rounded-full items-center justify-center h-9 w-9 text-primaryDark-200 hover:text-primaryDark-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
                        >
                          <RemoveIcon className="h-4 w-4" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={maxQty ?? undefined}
                          value={quantity}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleQuantityInputChange(item, e.target.value);
                          }}
                          onClick={stopInventoryCardToggle}
                          onMouseDown={stopInventoryCardToggle}
                          className="w-11 h-9 m-1 border-0 rounded-lg bg-white text-center font-open-sans text-[14px] text-black focus:outline-none focus:ring-1 focus:ring-primaryDark-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none touch-manipulation"
                        />
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateInventoryQuantity(item, 1);
                          }}
                          disabled={atMax}
                          className="flex border-[#E9E9E9] rounded-full items-center justify-center h-9 w-9 text-primaryDark-200 hover:text-primaryDark-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
                        >
                          <AddIcon className="h-4 w-4" />
                        </button>
                      </div>
                      {formatInventoryRemainingLabel(remainingQuantity, maxQty, item.measure_unit) && (
                        <span
                          data-testid="inventory-remaining-label"
                          className="font-open-sans text-[13px] text-green-600 ml-auto"
                        >
                          {formatInventoryRemainingLabel(remainingQuantity, maxQty, item.measure_unit)}
                        </span>
                      )}
                    </div>
                  )}

                  {!selected && !isOutOfStock && (
                    <div className="flex flex-row items-center mt-3 gap-2 flex-wrap">
                      {formatInventoryUnitPriceLabel(item.unit_price, item.measure_unit) && (
                        <span
                          data-testid="inventory-unit-price-label"
                          className="font-open-sans text-[14px] font-medium text-primaryDark-0/50"
                        >
                          {formatInventoryUnitPriceLabel(item.unit_price, item.measure_unit)}
                        </span>
                      )}
                      {formatInventoryRemainingLabel(remainingQuantity, maxQty, item.measure_unit) && (
                        <span
                          data-testid="inventory-remaining-label"
                          className="font-open-sans text-[13px] text-green-600 ml-auto"
                        >
                          {formatInventoryRemainingLabel(remainingQuantity, maxQty, item.measure_unit)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[#808080] font-open-sans text-[14px] py-4">
          No inventory items available for this kitchen.
        </p>
      )}
    </div>
  );
};

type FinalSubmissionViewProps = {
  view: string;
  resourceDetail: ResourceSchema;
  currentMember: MembershipSchema;
  setCurrentDate: any;
  startDate: any;
  setIsEditing: any;
  updateCurrentStep: any;
  endDate: any;
  plan: ResourcePlanSchema;
  selectedDay:any;
  nHours: number;
  additionalNotes: string;
  selectedTeamMembers: TeamMemberSchema[];
  orderInventory: OrderItemSchema[];
  bookingSelections: BookingSelection[];
  paymentMethod: PaymentMethod;
  paymentMethods: PaymentMethod[];
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod>>;
  selectedBlock?: TimeBlock;
  excludesSpace?: boolean;
};


const FinalSubmissionView: React.FC<FinalSubmissionViewProps> = ({
  resourceDetail,
  startDate,
  endDate,
  additionalNotes,
  selectedTeamMembers,
  orderInventory,
  setIsEditing,
  updateCurrentStep,
  nHours,
  plan,
  selectedDay,
  bookingSelections,
  currentMember,
  paymentMethod,
  paymentMethods,
  setPaymentMethod,
  selectedBlock,
  excludesSpace = false,

}) => {
  const handleEditBooking = (step: number) => {
      setIsEditing(true);
      updateCurrentStep(step);
    }

  const [isEditPay, setIsEditPay] = useState(false);
  const [isAddingPayMethod, setIsAddingPayMethod] = useState(false);
  const [isPlanSummaryOpen, setIsPlanSummaryOpen] = useState(true);
  const [isBookingDetailsOpen, setIsBookingDetailsOpen] = useState(true);
  const [isPaymentMethodOpen, setIsPaymentMethodOpen] = useState(true);
  const [showAllTeamMembers, setShowAllTeamMembers] = useState(false);

  const TEAM_MEMBERS_PREVIEW_COUNT = 1;
  const visibleTeamMembers = showAllTeamMembers
    ? selectedTeamMembers
    : selectedTeamMembers.slice(0, TEAM_MEMBERS_PREVIEW_COUNT);
  const hiddenTeamMembersCount = Math.max(
    selectedTeamMembers.length - TEAM_MEMBERS_PREVIEW_COUNT,
    0
  );

  const communityId = resourceDetail.community_id;
  const effectiveSelections = useMemo(
    () =>
      bookingSelections.length > 0
        ? bookingSelections
        : [{
            id: `${moment(selectedDay).format("YYYY-MM-DD")}-${moment(startDate).format("HH:mm")}-${moment(endDate).format("HH:mm")}`,
            date: moment(selectedDay).format("YYYY-MM-DD"),
            startTime: moment(startDate).format("HH:mm"),
            endTime: moment(endDate).format("HH:mm"),
            totalHours: nHours,
          }],
    [bookingSelections, selectedDay, startDate, endDate, nHours]
  );
  const syncedOrderInventory = useMemo(
    () => expandOrderInventoryForSelections(orderInventory, effectiveSelections),
    [orderInventory, effectiveSelections]
  );
  const inventorySummaryItems = useMemo(
    () => buildAggregatedInventorySummary(syncedOrderInventory),
    [syncedOrderInventory]
  );
  const uniqueDates = Array.from(new Set(effectiveSelections.map((selection) => selection.date))).sort(
    (a, b) => moment(a).valueOf() - moment(b).valueOf()
  );
  const baseHours = effectiveSelections[0]?.totalHours || 0;
  const sameHoursForAll = effectiveSelections.every(
    (selection) => selection.totalHours === baseHours
  );
  const hoursLabel = sameHoursForAll
    ? `${baseHours} hour${baseHours !== 1 ? "s" : ""}${effectiveSelections.length > 1 ? " each" : ""}`
    : "Varies by booking";
  let bookingDatesSummary: string | null = null;
  let bookingDatesLines: string[] | null = null;
  if (uniqueDates.length === 1) {
    bookingDatesSummary = formatLongBookingDateLabel(uniqueDates[0]);
  } else if (uniqueDates.length > 1) {
    if (areSortedBookingDatesConsecutive(uniqueDates)) {
      const start = formatLongBookingDateLabel(uniqueDates[0]);
      const end = formatLongBookingDateLabel(uniqueDates[uniqueDates.length - 1]);
      bookingDatesSummary = `${start} - ${end}`;
    } else {
      bookingDatesLines = uniqueDates.map(formatLongBookingDateLabel);
    }
  }
  const sameTimeForAll = effectiveSelections.every(
    (selection) =>
      selection.startTime === effectiveSelections[0]?.startTime &&
      selection.endTime === effectiveSelections[0]?.endTime
  );

  const getCardIcon = (paymentMethod: string) => {
    switch (paymentMethod) {
      case 'visa':
        return <VisaCardIcon className='w-8 my-auto' />;
      case 'mastercard':
        return <MasterCardIcon className='w-8 my-auto' />;
      case 'amex':
        return <AmexCardIcon className='w-8 my-auto' />;
      case 'discover':
        return <DiscoverCardIcon className='w-8 my-auto' />
      case 'cashapp':
        return <CashappIcon className='w-8 h-8 my-auto' />
      case 'amazon_pay':
        return <AmazonPayIcon className='w-8 h-6 my-auto' />
      default:
        return <img src="/icons/unknown.svg" alt="Unknown" />;
    }
  };

  // When payment method modal closes, also exit edit mode
  useEffect(() => {
    if (!isAddingPayMethod) {
      setIsEditPay(false);
    }
  }, [paymentMethod, paymentMethods, isAddingPayMethod, setIsEditPay])

  // useEffect(() => {
  //   // console.log("additionalNotes", additionalNotes)
  // }, [nHours, startDate, endDate, selectedDay, plan, additionalNotes, paymentMethod, resourceDetail, currentMember])
  
  return (
    <div className="flex flex-col w-full min-w-0 text-left mx-auto gap-y-4">
      {/* Booking Details */}
      <div className="flex flex-col space-y-4 p-3 w-full min-w-0 bg-[#F8F8F8] rounded-lg">
        
        {/* Plan Summary - Collapsible */}
        <div className="rounded-lg bg-white">
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsPlanSummaryOpen(!isPlanSummaryOpen)}
          >
            <h3 className="font-raleway font-semibold text-[16px] text-[#333333]">Plan Summary</h3>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${isPlanSummaryOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isPlanSummaryOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
              <div className="flex flex-col gap-2 pt-3">
                <div className="flex items-center space-x-2 min-w-0">
                  <div className="w-5 h-5 rounded-full bg-[#51A0B5] flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-[#333333] font-open-sans text-[14px] break-words">{plan.plan_name}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[#333333] font-open-sans font-semibold text-[14px]">
                    ${styleThousandCommas(plan.plan_rate || 0)} / {planRecurrenceMap[plan.plan_recurrence as keyof typeof planRecurrenceMap]}
                  </span>
                  <button 
                    className='shrink-0 text-primaryDark-400 border-0 text-[12px] font-open-sans hover:text-primaryDark-500 touch-manipulation' 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditBooking(1);
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Booking Details - Collapsible */}
        <div className="rounded-lg bg-white">
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsBookingDetailsOpen(!isBookingDetailsOpen)}
          >
            <h3 className="font-raleway font-semibold text-[16px] text-[#333333]">Booking Details</h3>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${isBookingDetailsOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isBookingDetailsOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
              {/* Hours */}
              <div className="flex items-start justify-between gap-3">
                <span className="shrink-0 text-gray-600 font-open-sans text-[14px]">Total Hours</span>
                <div className="flex min-w-0 items-center justify-end gap-2">
                  <span className="text-[#333333] font-open-sans font-semibold text-[14px] text-right break-words">{hoursLabel}</span>
                  <button 
                    className='shrink-0 border-0 text-[12px] font-open-sans text-primaryDark-400 hover:text-primaryDark-500 touch-manipulation' 
                    onClick={() => handleEditBooking(2)}
                  >
                    Edit
                  </button>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-start justify-between gap-3">
                <span className="shrink-0 pt-0.5 text-gray-600 font-open-sans text-[14px]">Date</span>
                <div className="flex min-w-0 flex-1 items-start justify-end gap-2">
                  <span className="text-[#333333] font-open-sans font-semibold text-[14px] text-right leading-snug break-words">
                    {bookingDatesLines ? (
                      <span className="flex flex-col items-end gap-0.5">
                        {bookingDatesLines.map((line, idx) => (
                          <span key={`${uniqueDates[idx]}-${idx}`}>{line}</span>
                        ))}
                      </span>
                    ) : (
                      bookingDatesSummary
                    )}
                  </span>
                  <button 
                    className="shrink-0 self-start border-0 pt-0.5 text-[12px] font-open-sans text-primaryDark-400 hover:text-primaryDark-500" 
                    onClick={() => handleEditBooking(3)}
                  >
                    Edit
                  </button>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start justify-between gap-3">
                <span className="shrink-0 pt-0.5 text-gray-600 font-open-sans text-[14px]">Time</span>
                <div className="flex min-w-0 flex-1 items-start justify-end gap-2">
                  <span className="text-[#333333] font-open-sans font-semibold text-[14px] text-right leading-snug break-words">
                    {sameTimeForAll
                      ? `${moment(effectiveSelections[0]?.startTime, "HH:mm").format("h:mm A")} - ${moment(effectiveSelections[0]?.endTime, "HH:mm").format("h:mm A")}`
                      : "Multiple time windows"}
                  </span>
                  <button 
                    className='shrink-0 self-start border-0 pt-0.5 text-[12px] font-open-sans text-primaryDark-400 hover:text-primaryDark-500 touch-manipulation' 
                    onClick={() => handleEditBooking(4)}
                  >
                    Edit
                  </button>
                </div>
              </div>

              {/* Team Members */}
              <div className="flex flex-col space-y-1 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-open-sans text-[14px]">Team Members</span>
                  <button
                    className='text-primaryDark-400 border-0 text-[12px] font-open-sans hover:text-primaryDark-500 touch-manipulation'
                    onClick={() => handleEditBooking(5)}
                  >
                    Edit
                  </button>
                </div>
                {selectedTeamMembers.length > 0 ? (
                  <div className="pt-2 space-y-2">
                    {visibleTeamMembers.map((member) => (
                      <div key={member.id} className="flex items-center gap-2">
                        {/* <TeamMemberAvatar member={member} size="sm" /> */}
                        <UserAvatar firstName={member.first_name} lastName={member.last_name} email={member.email} size="sm" />
                        <span className="text-[#333333] font-open-sans text-[12px]">
                          {getTeamMemberDisplayName(member)}
                        </span>
                      </div>
                    ))}
                    {!showAllTeamMembers && hiddenTeamMembersCount > 0 && (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-primaryDark-400 text-[12px] font-open-sans hover:text-primaryDark-500 touch-manipulation"
                        onClick={() => setShowAllTeamMembers(true)}
                      >
                        View {hiddenTeamMembersCount} more
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-[#808080] font-open-sans text-[12px] pt-2">No team members added</p>
                )}
              </div>

              {/* Inventory */}
              <div className="flex flex-col space-y-1 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-open-sans text-[14px]">Inventory</span>
                  <button
                    className='text-primaryDark-400 border-0 text-[12px] font-open-sans hover:text-primaryDark-500 touch-manipulation'
                    onClick={() => handleEditBooking(6)}
                  >
                    Edit
                  </button>
                </div>
                {inventorySummaryItems.length > 0 ? (
                  <div className="pt-2 space-y-1">
                    {inventorySummaryItems.map((item) => (
                      <p
                        key={item.key}
                        className="text-[#333333] font-open-sans text-[12px]"
                      >
                        <AggregatedInventoryLabel item={item} />
                        {/* {' '}- {formatInventoryLineTotal(item.unit_price, item.totalQuantity)} */}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#808080] font-open-sans text-[12px] pt-2">No items selected</p>
                )}
              </div>

              {/* Additional Notes */}
              <div className="flex flex-col space-y-1 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-open-sans text-[14px]">Additional Notes</span>
                  <button 
                    className='text-primaryDark-400 border-0 text-[12px] font-open-sans hover:text-primaryDark-500 touch-manipulation' 
                    onClick={() => handleEditBooking(7)}
                  >
                    Edit
                  </button>
                </div>
                {(additionalNotes) && <p className="text-[#333333] font-open-sans text-[12px] pt-2">{additionalNotes}</p>}
              </div>
        
            </div>
          )}
        </div>

        {/* Payment Method - Collapsible */}
        <div className="rounded-lg bg-white">
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsPaymentMethodOpen(!isPaymentMethodOpen)}
          >
            <h3 className="font-raleway font-semibold text-[16px] text-[#333333]">Payment Method</h3>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${isPaymentMethodOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isPaymentMethodOpen && (
            <div className="px-4 pb-4 border-t border-gray-100">
              {(!isEditPay && (paymentMethod && paymentMethod?.card)) ? (
                <div className="flex items-center justify-between pt-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-7 border-[#E9E9E9] border-[1px] p-1 rounded-xl flex items-center justify-center">
                      {getCardIcon(paymentMethod.card.brand)} 
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#333333] font-open-sans font-semibold text-[14px]">
                        {paymentMethod?.card?.brand?.toUpperCase()}
                      </span>
                      <span className="text-gray-500 font-open-sans text-[12px]">
                        **** {paymentMethod?.card?.last4} &nbsp;
                        {paymentMethod?.card?.exp_month?.toString().padStart(2, '0')}/{paymentMethod?.card?.exp_year?.toString().slice(-2)}
                      </span>
                    </div>
                  </div>
                  <button 
                    className='text-primaryDark-400 border-0 text-[12px] font-open-sans hover:text-primaryDark-500' 
                    onClick={() => setIsEditPay(true)}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="pt-3">
                  <PaymentsView 
                    communityId={communityId}
                    paymentMethods={paymentMethods}
                    paymentMethod={paymentMethod} 
                    setPaymentMethod={setPaymentMethod}
                    setIsEditPay={setIsEditPay} 
                    isAddingPayMethod={isAddingPayMethod}
                    setIsAddingPayMethod={setIsAddingPayMethod}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="w-full min-w-0 bg-[#F8F8F8] p-3 rounded-lg">
        <PriceDetails
          plan={plan}
          startDate={startDate}
          endDate={endDate}
          currentKitchen={resourceDetail}
          member={currentMember}
          hoursDiff={nHours}
          selectedBlock={selectedBlock}
          bookingSelections={effectiveSelections}
          orderInventory={orderInventory}
          excludesSpace={excludesSpace}
        />
      </div>
    </div>
  );
};

type PaymentsViewProps = {
  communityId?: string;
  paymentMethod: PaymentMethod;
  paymentMethods: PaymentMethod[];
  isAddingPayMethod: boolean;
  setIsAddingPayMethod: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod>>;
  setIsEditPay: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PaymentsView: React.FC<PaymentsViewProps> = ({
  // communityId,
  paymentMethod,
  paymentMethods,
  isAddingPayMethod,
  setIsEditPay,
  setPaymentMethod,
  setIsAddingPayMethod
}) => {
  const { user } = useCompleteSignIn();
  const loggedinUid = (user && user?.uid) ? user.uid : ''
  // var { paymentMethods } = useGetPaymentMethods(loggedinUid);
  // paymentMethods = (paymentMethods && Array.isArray(paymentMethods)) ? paymentMethods : [];

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMethod = paymentMethods.find((pm:any) => pm.id === e.target.value);
    setPaymentMethod(selectedMethod || {} as PaymentMethod);
    setIsEditPay(false);
  };

  // const [isAddPay, setIsAddPay] = useState(false);

  // Keep local selection in sync with refreshed list (after add/remove)
  useEffect(() => {
    const hasMethods = paymentMethods && paymentMethods.length > 0;
    const currentId = (paymentMethod as any)?.id;
    const stillExists = hasMethods ? paymentMethods.some((pm: any) => pm?.id === currentId) : false;
    if (hasMethods && (!currentId || !stillExists)) {
      setPaymentMethod(paymentMethods[paymentMethods.length - 1]);
      setIsEditPay(false);
    }
  }, [paymentMethods, paymentMethod, setPaymentMethod, setIsEditPay])

  return (
    <div className="flex flex-col w-full h-full text-left my-2">
      <h1 className='cursor-pointer text-primaryDark-400 ml-auto text-sm'  onClick={() => setIsAddingPayMethod(prev => !prev)}>
        {((!isAddingPayMethod) && (paymentMethods && paymentMethods.length > 0)) ? '+ Add Payment Method' : 'Add Payment Method'}
      </h1>

      {
        (!isAddingPayMethod) ? ((paymentMethods && paymentMethods.length > 0) ? 
        (
          <select 
            value={paymentMethod?.id}
            onChange={handlePaymentMethodChange}
            className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#808080] font-open-sans p-2"
          >
            <option value="">Choose a payment method</option>
            {paymentMethods.filter((p: any) => p && p?.card && p?.card?.brand).map((pm: any) => (
              <option key={pm.id} value={pm.id}>
                {
                  (pm && pm?.card && pm?.card?.brand ) && (`${pm.card.brand.toUpperCase()} **** ${pm.card.last4} ${pm.card.exp_month.toString().padStart(2, '0')}/${pm.card.exp_year.toString().slice(-2)}`)
                }
              </option>
            ))}
          </select>
        ) : (
        <p className="font-open-sans font-light text-gray-500 text-sm">No Existing Payment Method</p>
        )) : (
          // <StripePaymentCheckOut communityId={communityId} setIsAddingPayMethod={setIsAddPay} uid={loggedinUid} mode="setup" currency="usd" setup_future_usage='off_session' isAddPayMethod={true} hasExistingPayMethod={paymentMethods && paymentMethods.length > 0} />
          <AddPaymentMethodModal loggedinUid={loggedinUid} isOpen={isAddingPayMethod} setIsOpen={()=>setIsAddingPayMethod(false)} />
        )
      }
    </div>
  );
};
export const useDate = () => {
  const locale = "en";
  const [today, setDate] = React.useState(new Date()); // Save the current date to be able to trigger an update

  React.useEffect(() => {
    const timer = setInterval(() => {
      // Creates an interval which will update the current data every minute
      // This will trigger a rerender every component that uses the useDate hook.
      setDate(new Date());
    }, 60 * 1000);
    return () => {
      clearInterval(timer); // Return a funtion to clear the timer so that it will stop being called on unmount
    };
  }, []);

  const day = today.toLocaleDateString(locale, { weekday: "long" });
  const date = `${day}, ${today.getDate()} ${today.toLocaleDateString(locale, {
    month: "long",
  })}\n\n`;

  const hour = today.getHours();
  const wish = `Good ${
    (hour < 12 && "Morning") || (hour < 17 && "Afternoon") || "Evening"
  }, `;

  const time = today.toLocaleTimeString(locale, {
    hour: "numeric",
    hour12: true,
    minute: "numeric",
  });

  return {
    date,
    time,
    wish,
  };
};



type PriceDetailsProps = {
  member: MembershipSchema;
  plan: ResourcePlanSchema;
  hoursDiff: number;
  bookingSelections?: BookingSelection[];
  currentKitchen: ResourceSchema;
  startDate: string;
  endDate: string;
  selectedBlock?: TimeBlock;
  showConfirmButton?: boolean;
  onConfirm?: () => void;
  orderInventory?: OrderItemSchema[];
  excludesSpace?: boolean;
  // handleClose: () => void;
  // toggleUpdatePlan: (value: boolean) => void;
}

const PriceDetails: React.FC<PriceDetailsProps> = ({member, plan, hoursDiff, bookingSelections = [], currentKitchen, startDate, endDate, selectedBlock, showConfirmButton = false, onConfirm, orderInventory = [], excludesSpace = false}) => {

  // Debug: Log when props change
  useEffect(() => {
    // console.log('PriceDetails Props Changed:', {
    //   startDate,
    //   endDate,
    //   hoursDiff,
    //   planRate: plan?.plan_rate,
    //   discountsCount: currentKitchen?.discounts?.length || 0
    // });
  }, [startDate, endDate, hoursDiff, plan, currentKitchen]);

  const currentMemberBookingPlanId = getMemberBookingPlanIdForResource(member, plan?.resource_id || '', currentKitchen?.plans)
  const currentMemberBookingBalance = getMemberBookingBalanceForResource(member, plan?.resource_id || '', plan?.plan_type)

  // console.log("currentMemberBookingPlanId", currentMemberBookingPlanId)
  // console.log("currentMemberBookingBalance", currentMemberBookingBalance)
  
  const hasMultipleSelections = bookingSelections.length > 1;
  const selectionCount = bookingSelections.length > 0 ? bookingSelections.length : 1;

  // Memoize subtotal cost calculation
  const subtotalCost = useMemo(() => {
    if (excludesSpace) {
      return 0;
    }

    const isFirstPlanBooking = isFirstBookingWithPlan(member?.username || '', member, plan?.resource_id || '', plan?.plan_id || '', undefined, currentKitchen?.plans);

    if (bookingSelections.length > 0) {
      if (plan?.plan_recurrence === 'hourly') {
        return bookingSelections.reduce((acc, selection) => {
          const selectionStart = moment(`${selection.date}T${selection.startTime}`);
          let selectionEnd = moment(`${selection.date}T${selection.endTime}`);
          if (!selectionEnd.isAfter(selectionStart)) {
            selectionEnd = selectionEnd.add(1, 'day');
          }
          const pricingResult = calculateMixedTimeBlockCost(
            { start: selectionStart, end: selectionEnd },
            plan,
            currentKitchen?.resource_options
          );
          return acc + pricingResult.totalCost;
        }, 0);
      }
      return isFirstPlanBooking ? (plan?.plan_rate ?? 0) * bookingSelections.length : 0;
    }

    // Use time-based pricing for hourly plans with selectedBlock
    if (plan?.plan_recurrence === 'hourly' && selectedBlock && !hasMultipleSelections) {
      const pricingResult = calculateMixedTimeBlockCost(selectedBlock, plan, currentKitchen?.resource_options);
      return pricingResult.totalCost;
    }
    
    // Fallback to original logic for non-hourly plans or when no selectedBlock
    const baseCost = (plan?.plan_rate && plan?.plan_recurrence === 'hourly') 
      ? plan.plan_rate * hoursDiff 
      : isFirstPlanBooking ? (plan?.plan_rate ?? 0) : 0;
    
    return baseCost;
  }, [plan, hoursDiff, member, selectedBlock, currentKitchen, bookingSelections, hasMultipleSelections, excludesSpace]);
  
  // const hoursBalance = useMemo(() => 
  //   (plan?.plan_recurrence && member?.member_balance && currentKitchen?.capacity) 
  //     ? maxHours(parseInt(currentKitchen.capacity), plan.plan_recurrence, member?.member_balance) - hoursDiff 
  //     : 0,
  //   [plan?.plan_recurrence, member?.member_balance, currentKitchen?.capacity, hoursDiff]
  // );

  const checkDiscountActive = useCallback((discount: DiscountSchema): boolean => {
    if (!discount?.rate) return false;
    if (!startDate || !endDate) return false;
    
    const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
    const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
    const bookingStart = moment(startDate).startOf('day');
    const bookingEnd = moment(endDate).endOf('day');
    
    // Check if the booking period falls entirely within the discount period
    const isValid = bookingStart.isSameOrAfter(discountStartDate) && 
                    bookingStart.isSameOrBefore(discountEndDate) &&
                    bookingEnd.isSameOrAfter(discountStartDate) &&
                    bookingEnd.isSameOrBefore(discountEndDate);
    
    // console.log('Discount Check:', {
    //   discountName: discount.name,
    //   discountStart: discountStartDate.format('YYYY-MM-DD'),
    //   discountEnd: discountEndDate.format('YYYY-MM-DD'),
    //   bookingStart: bookingStart.format('YYYY-MM-DD'),
    //   bookingEnd: bookingEnd.format('YYYY-MM-DD'),
    //   isValid
    // });
    
    return isValid;
  }, [startDate, endDate]);
  
  // const cleaningCost = 5
  // const serviceCost = 3
  // const equipmentCost = 0
  const feesCost = useMemo(() => {
    const baseFees = currentKitchen.fees && currentKitchen.fees.length > 0
      ? currentKitchen.fees.reduce((acc: number, fee: FeeSchema) => acc + fee.rate, 0)
      : 0;
    return baseFees * selectionCount;
  }, [currentKitchen.fees, selectionCount]);
  
  const discountAmount = useMemo(() => {
    if (!currentKitchen.discounts || currentKitchen.discounts.length === 0) return 0;

    if (bookingSelections.length > 0) {
      return bookingSelections.reduce((selectionTotal, selection) => {
        const selectionStart = moment(`${selection.date}T${selection.startTime}`).startOf('day');
        const selectionEnd = moment(`${selection.date}T${selection.endTime}`).endOf('day');
        const selectionSubtotal = plan?.plan_recurrence === 'hourly'
          ? (plan?.plan_rate || 0) * (selection.totalHours || 0)
          : (plan?.plan_rate || 0);

        const selectionDiscount = currentKitchen.discounts?.reduce((acc: number, discount: DiscountSchema) => {
          const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
          const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
          const isActive = selectionStart.isSameOrAfter(discountStartDate) &&
            selectionStart.isSameOrBefore(discountEndDate) &&
            selectionEnd.isSameOrAfter(discountStartDate) &&
            selectionEnd.isSameOrBefore(discountEndDate);
          if (!isActive || !discount?.rate) return acc;

          if (discount.discount_type === 'fixed') return acc + discount.rate;
          if (discount.discount_type === 'percentage') return acc + (discount.rate * selectionSubtotal / 100);
          return acc;
        }, 0) || 0;

        return selectionTotal + selectionDiscount;
      }, 0);
    }

    const amount = currentKitchen.discounts.reduce((acc: number, discount: DiscountSchema) => {
      if (checkDiscountActive(discount)) {
        if (discount.discount_type === 'fixed') {
          return acc + discount.rate;
        } else if (discount.discount_type === 'percentage') {
          return acc + (discount.rate * subtotalCost / 100);
        }
      }
      return acc;
    }, 0);
    
    // console.log('Discount Amount Recalculated:', {
    //   amount,
    //   subtotalCost,
    //   startDate,
    //   endDate,
    //   hoursDiff
    // });
    
    return amount;
  }, [currentKitchen.discounts, checkDiscountActive, subtotalCost, bookingSelections, plan?.plan_rate, plan?.plan_recurrence]);

  const activeDiscountRows = useMemo(() => {
    if (!currentKitchen.discounts || currentKitchen.discounts.length === 0) return [] as { key: string; label: string; amount: number }[];

    if (bookingSelections.length > 0) {
      return currentKitchen.discounts
        .map((discount: DiscountSchema, index: number) => {
          const amount = bookingSelections.reduce((selectionTotal, selection) => {
            const selectionStart = moment(`${selection.date}T${selection.startTime}`).startOf('day');
            const selectionEnd = moment(`${selection.date}T${selection.endTime}`).endOf('day');
            const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
            const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
            const isActive = selectionStart.isSameOrAfter(discountStartDate) &&
              selectionStart.isSameOrBefore(discountEndDate) &&
              selectionEnd.isSameOrAfter(discountStartDate) &&
              selectionEnd.isSameOrBefore(discountEndDate);
            if (!isActive || !discount?.rate) return selectionTotal;

            const selectionSubtotal = plan?.plan_recurrence === 'hourly'
              ? (plan?.plan_rate || 0) * (selection.totalHours || 0)
              : (plan?.plan_rate || 0);
            if (discount.discount_type === 'fixed') return selectionTotal + discount.rate;
            if (discount.discount_type === 'percentage') return selectionTotal + (discount.rate * selectionSubtotal / 100);
            return selectionTotal;
          }, 0);

          return {
            key: `discount-${discount.id || index}`,
            label: discount.name || 'Welcome bonus',
            amount,
          };
        })
        .filter((row) => row.amount > 0);
    }

    return currentKitchen.discounts
      .filter((discount: DiscountSchema) => checkDiscountActive(discount) && subtotalCost)
      .map((discount: DiscountSchema, index: number) => ({
        key: `discount-${discount.id || index}`,
        label: discount.name || 'Welcome bonus',
        amount:
          (discount.rate && discount.discount_type === 'percentage')
            ? (discount.rate * subtotalCost / 100)
            : (discount.rate && discount.discount_type === 'fixed')
            ? discount.rate
            : 0,
      }))
      .filter((row) => row.amount > 0);
  }, [bookingSelections, checkDiscountActive, currentKitchen.discounts, plan?.plan_rate, plan?.plan_recurrence, subtotalCost]);

  const hasHourlyMixedRates = plan?.plan_recurrence === 'hourly' && bookingSelections.length > 0;
  const mixedRateBreakdown = useMemo(() => {
    if (!hasHourlyMixedRates) {
      return [] as Array<{ key: string; period: string; hours: number; rate: number; cost: number }>;
    }

    const breakdownMap = new Map<string, { period: string; hours: number; rate: number; cost: number }>();
    bookingSelections.forEach((selection) => {
      const selectionStart = moment(`${selection.date}T${selection.startTime}`);
      let selectionEnd = moment(`${selection.date}T${selection.endTime}`);
      if (!selectionEnd.isAfter(selectionStart)) {
        selectionEnd = selectionEnd.add(1, 'day');
      }

      const pricingResult = calculateMixedTimeBlockCost(
        { start: selectionStart, end: selectionEnd },
        plan,
        currentKitchen?.resource_options
      );

      pricingResult.breakdown.forEach((item) => {
        const key = `${item.period}-${item.rate}`;
        const existing = breakdownMap.get(key);
        if (existing) {
          existing.hours += item.hours;
          existing.cost += item.cost;
        } else {
          breakdownMap.set(key, {
            period: item.period,
            hours: item.hours,
            rate: item.rate,
            cost: item.cost,
          });
        }
      });
    });

    const periodOrder: Record<string, number> = { PEAK: 0, OFF_PEAK: 1, STANDARD: 2 };
    return Array.from(breakdownMap.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => (periodOrder[a.period] ?? 99) - (periodOrder[b.period] ?? 99));
  }, [bookingSelections, currentKitchen?.resource_options, hasHourlyMixedRates, plan]);

  
  // helper to check if discount is active, i.e. discount start and end date is between booking start and end date
  // const isDiscountActive = currentKitchen.discounts && currentKitchen.discounts.length > 0 ? currentKitchen.discounts.some((discount: DiscountSchema) => {
  //   return checkDiscountActive(discount)
  // }) : false;

  const syncedOrderInventory = useMemo(
    () => expandOrderInventoryForSelections(orderInventory, bookingSelections),
    [orderInventory, bookingSelections]
  );

  const inventoryCostSummary = useMemo(
    () => buildAggregatedInventorySummary(syncedOrderInventory),
    [syncedOrderInventory]
  );

  const applyInventoryLimits = shouldApplyMemberInventoryLimits(plan, member);
  const memberInventoryLimits = useMemo(
    () => parseMemberInventoryLimits(member?.inventory_limits),
    [member?.inventory_limits]
  );

  const inventoryCostBreakdown = useMemo(
    () =>
      calculateInventoryCostLinesWithLimits(
        inventoryCostSummary.map((item) => ({
          inventoryId: item.key,
          totalQuantity: item.totalQuantity,
          unitPrice: item.unit_price,
        })),
        memberInventoryLimits,
        applyInventoryLimits
      ),
    [applyInventoryLimits, inventoryCostSummary, memberInventoryLimits]
  );

  const inventoryCost = inventoryCostBreakdown.inventoryCost;
  const inventoryCostById = useMemo(() => {
    const map = new Map<string, (typeof inventoryCostBreakdown.lines)[number]>();
    inventoryCostBreakdown.lines.forEach((line) => {
      map.set(line.inventoryId, line);
    });
    return map;
  }, [inventoryCostBreakdown.lines]);

  const totalCost = useMemo(() => {
    // For users with booking balance, they still pay fees but not the base cost
    const effectiveSubtotal = 
    (currentMemberBookingBalance && currentMemberBookingPlanId && currentMemberBookingPlanId === plan?.plan_id && !['hourly', 'daily'].includes(plan?.plan_recurrence || '')) 
      ? 0 
      : 
      subtotalCost;
    
    const baseTotal = effectiveSubtotal + feesCost + inventoryCost;
    const finalTotal = baseTotal - discountAmount;
    return Math.max(0, finalTotal); // Ensure total is never negative
  }, [subtotalCost, discountAmount, feesCost, inventoryCost, currentMemberBookingBalance, currentMemberBookingPlanId, plan?.plan_id, plan?.plan_recurrence]);

  // State for discount code input
  // const [discountCode, setDiscountCode] = useState('');

  // console.log("PriceDetails Debug:", {
  //   subtotalCost,
  //   feesCost,
  //   discountAmount,
  //   totalCost,
  //   currentMemberBookingBalance,
  //   currentMemberBookingPlanId,
  //   planId: plan?.plan_id,
  //   hoursDiff,
  //   planRate: plan?.plan_rate
  // });

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col w-full text-left  bg-white rounded-lg px-3 py-2 my-1">
        {/* Header */}
        <h2 className="text-[#555555] font-raleway font-medium text-[16px] mb-3">Cost Breakdown</h2>
        
        {/* Price items with checkmarks */}
        <div className="flex flex-col space-y-3.5 mb-7">
          {!excludesSpace && (
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start space-x-3 min-w-0 flex-1">
              <div className="w-5 h-5 rounded-full bg-[#4DB8D8] flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[#555555] font-open-sans text-[15px] font-normal">Subtotal</span>
                {hasHourlyMixedRates ? (
                  <div className="text-[#999999] font-open-sans text-[12px] font-normal space-y-1">
                    {mixedRateBreakdown.map((row) => (
                      <p key={row.key}>
                        {row.period === 'PEAK' ? 'Peak' : row.period === 'OFF_PEAK' ? 'Off-Peak' : 'Standard'}:
                        {" "}{row.hours.toFixed(1)}h x ${row.rate.toFixed(2)} = ${row.cost.toFixed(2)}
                      </p>
                    ))}
                  </div>
                ) : (selectedBlock && plan?.plan_recurrence === 'hourly' ? (
                  <div className="text-[#999999] font-open-sans text-[12px] font-normal">
                    {(() => {
                      const pricingResult = calculateMixedTimeBlockCost(selectedBlock, plan, currentKitchen?.resource_options);
                      if (pricingResult.breakdown.length === 1) {
                        const item = pricingResult.breakdown[0];
                        return `${item.period === 'PEAK' ? 'Peak' : item.period === 'OFF_PEAK' ? 'Off-Peak' : 'Standard'} rate: $${item.rate.toFixed(2)} / hr`;
                      } else {
                        return `Mixed rates: ${pricingResult.breakdown.map((item: { period: string; hours: number; rate: number }) =>
                          `${item.period === 'PEAK' ? 'Peak' : item.period === 'OFF_PEAK' ? 'Off-Peak' : 'Standard'} (${item.hours.toFixed(1)}h @ $${item.rate.toFixed(2)})`
                        ).join(', ')}`;
                      }
                    })()}
                  </div>
                ) : (<></>))}
              </div>
            </div>
            <span className="shrink-0 text-[#1F1F1F] font-open-sans font-semibold text-[15px] text-right">
              
              {
                (currentMemberBookingBalance && currentMemberBookingPlanId && currentMemberBookingPlanId === plan?.plan_id && !['hourly', 'daily'].includes(plan?.plan_recurrence || '')) ? (
                  <>
                  <span className="text-[#1F1F1F] flex flex-row items-center justify-between font-open-sans font-semibold ">
                    <span className="text-[#1F1F1F] line-through text-[12px]">
                      ${(plan?.plan_rate && plan?.plan_recurrence === 'hourly') ? styleThousandCommas((plan.plan_rate * hoursDiff).toFixed(2)) : (plan?.plan_rate ? styleThousandCommas((plan.plan_rate).toFixed(2)) : '0')}
                    </span>
                    &nbsp; <span className="text-[#1F1F1F] font-open-sans text-[15px]">$0.00</span>
                  </span>
                  <p className="font-normal font-open-sans text-primaryDark-300/70 text-[11px] text-left w-full">
                    {/* {currentMemberBookingPlanId} */}
                    {currentMemberBookingBalance} hours left
                  </p>
                  </>
                ) : (
                  <>
                    {/* <p className="font-normal font-open-sans text-primaryDark-300/70 text-[11px] text-left w-full">
                      NEW
                    </p> */}
                    ${styleThousandCommas(subtotalCost.toFixed(2))}
                  </>
                )

              }
            </span>
          </div>
          )}

          {/* Fees */}
          {currentKitchen.fees && currentKitchen.fees.length > 0 && (
            <>
              {currentKitchen.fees.map((fee: FeeSchema, index: number) => (
                <div key={index} className="flex items-start justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-5 h-5 rounded-full bg-[#4DB8D8] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-[#555555] font-open-sans text-[15px] font-normal break-words">
                      <span className="capitalize">{fee.name}</span>
                      {selectionCount > 1 ? ` x${selectionCount}` : ""}
                    </span>
                  </div>
                  <span className="shrink-0 text-[#1F1F1F] font-open-sans font-semibold text-[15px] text-right">
                    ${fee.rate ? (fee.rate * selectionCount).toFixed(2) : '0'}
                  </span>
                </div>
              ))}
            </>
          )}

          {inventoryCostSummary.map((item) => {
            const line = inventoryCostById.get(item.key);
            const fullPrice = ((item.unit_price ?? 0) * item.totalQuantity);
            const includedQty = line?.includedQty ?? 0;
            const billableQty = line?.billableQty ?? item.totalQuantity;
            const lineCost = line?.lineCost ?? fullPrice;
            const remainingAfterThis = line?.remainingAfterThis ?? 0;
            const isFullyIncluded = includedQty > 0 && billableQty === 0;
            const isPartialIncluded = includedQty > 0 && billableQty > 0;

            return (
            <div key={`inventory-${item.key}`} className="flex items-start justify-between gap-3">
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <div className="w-5 h-5 rounded-full bg-[#4DB8D8] flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[#555555] font-open-sans text-[15px] font-normal break-words">
                    <span>
                      {item.item_name} x{item.quantityPerBooking}
                    </span>
                    {item.bookingCount > 1 && (
                      <span className="text-[#999999] font-open-sans text-[11px] font-normal">
                        {' '}
                        ({item.bookingCount} bookings)
                      </span>
                    )}
                  </span>
                  {isFullyIncluded && (
                    <p className="font-normal font-open-sans text-primaryDark-300/70 text-[11px] text-left w-full">
                      {remainingAfterThis > 0
                        ? `${remainingAfterThis} included left`
                        : `${includedQty} included`}
                    </p>
                  )}
                  {isPartialIncluded && (
                    <p className="font-normal font-open-sans text-primaryDark-300/70 text-[11px] text-left w-full">
                      {includedQty} included / {billableQty} charged
                    </p>
                  )}
                </div>
              </div>
              <span className="shrink-0 text-[#1F1F1F] font-open-sans font-semibold text-[15px] text-right">
                {isFullyIncluded ? (
                  <span className="flex flex-col items-end">
                    <span className="line-through text-[#999999] text-[12px] font-normal">
                      ${fullPrice.toFixed(2)}
                    </span>
                    <span>$0.00</span>
                  </span>
                ) : (
                  `$${lineCost.toFixed(2)}`
                )}
              </span>
            </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col w-full text-left bg-white rounded-lg px-3 py-2 my-1">
        {/* Apply Discount Section */}
        <div className="flex flex-col space-y-2.5 mb-3">
          <label className="text-[#555555] font-open-sans text-[15px] font-normal">
            Booking Discount 
            {/* <span className="text-[#999999]">(Optional)</span> */}
          </label>
          {/* <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="1w43ty"
              className="flex-1 px-3.5 py-2.5 border border-[#CCCCCC] rounded-md text-[14px] font-open-sans text-[#333333] placeholder-[#AAAAAA] focus:outline-none focus:ring-2 focus:ring-[#4DB8D8] focus:border-[#4DB8D8] transition-all"
            />
            <button className="px-7 py-2.5 bg-[#0A5C6E] hover:bg-[#0D7287] text-white font-raleway font-semibold text-[14px] rounded-md transition-colors duration-150">
              Apply
            </button>
          </div> */}
        </div>
      

        {/* Active Discounts */}
        {activeDiscountRows.length > 0 && (
          <div className="flex flex-col space-y-3.5 mb-7">
            {activeDiscountRows.map((row) => (
                <div key={row.key} className="flex items-start justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-5 h-5 rounded-full bg-[#4DB8D8] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-[#555555] font-open-sans text-[15px] font-normal break-words">
                      {row.label}
                    </span>
                  </div>
                  <span className="shrink-0 text-[#1F1F1F] font-open-sans font-semibold text-[15px] text-right">
                    ($${row.amount.toFixed(2)})
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>


      <div className="flex flex-col w-full text-left  bg-white rounded-lg px-3 py-2 my-1">
        {/* Total Section */}
        <div className="border-0">
          <div className="flex items-center justify-between gap-3">
            <span className="shrink-0 text-[#1F1F1F] font-raleway font-medium text-[16px]">Total</span>
            <span className="shrink-0 text-[#1F1F1F] font-raleway font-bold text-[20px] text-right">
              ${styleThousandCommas(totalCost.toFixed(2))}
            </span>
          </div>
        </div>
      </div>

      {/* Confirm and Pay Button */}
      {showConfirmButton && onConfirm && (
        <button
          onClick={onConfirm}
          className="w-full py-3.5 border-0 bg-[#0A5C6E] hover:bg-[#0D7287] text-white font-raleway font-semibold text-[15px] rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
        >
          Confirm and Pay
        </button>
      )}
    </div>
  )
}

export default RequestKitchen;



